// ══════════════════════════════════════════════════════
// Strategy Lab — Builder Tab v5 (Shared ParameterForm)
// ══════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useRef, createContext, useContext } from "react";
import { classifyFormField, classifyRangeFamily, BADGES, TUNABLE_FIXED_FIELDS, STRUCTURE_MODE_FIELDS } from "./fieldProvenance";
import { theme, fonts } from "../../theme";
import { Card, CardHeader, Button, Pill, Badge, LoadingState } from "../../components/ui";
import { toast, showModal } from "../../components/shared";
import { API_BASE } from "../../config";
import { useApi } from "../../hooks/useApi";
import { classifyStall } from "./onboardWatchdog";
import { classifyCompletion } from "./onboardCompletion";
import {
  ALL_INSTRUMENTS, TFS, INST_META, DEF_FORM,
  parseYamlToForm, formToConfig, validateForm, formToYamlString,
  postApi, ok, inp, sel, lbl, grpH, grpDesc,
} from "./constants";

// ── GoalsEditor ───────────────────────────────────────────────────────────────
// Fully editable panel for IS/OOS constraints + score weights + normalization.
// Renders as a clean two-column grid. All changes patch form.goals directly.

// Goals defaults — used by Reset button
const GOALS_DEFAULTS = {
  hard_constraints_is: { min_trades:30, min_profit_factor:1.25, max_drawdown_pct:15.0, min_win_rate:0.25 },
  hard_constraints_oos: { min_window_pnl_usd:-1000, max_single_window_loss_usd:2000, max_rolling_3window_loss_usd:5000, max_total_drawdown_pct:10, min_total_oos_pnl_usd:750, min_profitable_windows_pct:65, min_reliable_windows_pct:60 },
  oos_consistent_pnl_config: { pnl_metric:"avg_pnl_per_trade", pnl_weight:0.35, sharpe_weight:0.30, profitable_pct_weight:0.20, consistency_weight:0.15, pnl_norm:30.0, sharpe_norm:1.5, profitable_pct_target:0.65, reliability_multiplier_quality_floor:0.6 },
};

// ── Goals Templates ───────────────────────────────────────────────────────────
const GOALS_TEMPLATES = [
  {
    key: "standard",
    label: "Standard",
    icon: "⚖️",
    desc: "Empfohlener Ausgangspunkt. Ausgewogene Constraints für realistische Qualitäts­anforderungen.",
    color: "#2563eb",
    hard_constraints_is:  { min_trades:30, min_profit_factor:1.25, max_drawdown_pct:15.0, min_win_rate:0.25 },
    hard_constraints_oos: { min_window_pnl_usd:-1000, max_single_window_loss_usd:2000, max_rolling_3window_loss_usd:5000, max_total_drawdown_pct:10, min_total_oos_pnl_usd:750, min_profitable_windows_pct:65, min_reliable_windows_pct:60 },
    oos_consistent_pnl_config: { pnl_weight:0.35, sharpe_weight:0.30, profitable_pct_weight:0.20, consistency_weight:0.15, pnl_norm:30.0, sharpe_norm:1.5, profitable_pct_target:0.65 },
  },
  {
    key: "conservative",
    label: "Konservativ",
    icon: "🛡️",
    desc: "Für Live-Trading mit echtem Kapital. Enge Constraints — weniger Trials bestehen, aber deutlich robustere Ergebnisse.",
    color: "#16a34a",
    hard_constraints_is:  { min_trades:40, min_profit_factor:1.4, max_drawdown_pct:12.0, min_win_rate:0.28 },
    hard_constraints_oos: { min_window_pnl_usd:-500, max_single_window_loss_usd:1500, max_rolling_3window_loss_usd:3500, max_total_drawdown_pct:8, min_total_oos_pnl_usd:1500, min_profitable_windows_pct:70, min_reliable_windows_pct:65 },
    oos_consistent_pnl_config: { pnl_weight:0.30, sharpe_weight:0.35, profitable_pct_weight:0.20, consistency_weight:0.15, pnl_norm:25.0, sharpe_norm:1.3, profitable_pct_target:0.70 },
  },
  {
    key: "exploration",
    label: "Exploration",
    icon: "🔍",
    desc: "Neue Strategie kennenlernen. Lockere Constraints — viele Trials bestehen, damit du die Parameterland­schaft verstehst. Nicht für Live-Trading.",
    color: "#7c3aed",
    hard_constraints_is:  { min_trades:20, min_profit_factor:1.05, max_drawdown_pct:25.0, min_win_rate:0.15 },
    hard_constraints_oos: { min_window_pnl_usd:-2000, max_single_window_loss_usd:5000, max_rolling_3window_loss_usd:10000, max_total_drawdown_pct:20, min_total_oos_pnl_usd:0, min_profitable_windows_pct:50, min_reliable_windows_pct:45 },
    oos_consistent_pnl_config: { pnl_weight:0.40, sharpe_weight:0.25, profitable_pct_weight:0.20, consistency_weight:0.15, pnl_norm:20.0, sharpe_norm:2.0, profitable_pct_target:0.50 },
  },
  {
    key: "wf_focus",
    label: "WF-Fokus",
    icon: "📊",
    desc: "Walk-Forward im Vordergrund. Verschärfte OOS-Constraints, IS-Hürde moderat. Ideal vor einem geplanten WF-Run.",
    color: "#0891b2",
    hard_constraints_is:  { min_trades:25, min_profit_factor:1.15, max_drawdown_pct:18.0, min_win_rate:0.22 },
    hard_constraints_oos: { min_window_pnl_usd:-500, max_single_window_loss_usd:1500, max_rolling_3window_loss_usd:3000, max_total_drawdown_pct:8, min_total_oos_pnl_usd:1200, min_profitable_windows_pct:72, min_reliable_windows_pct:68 },
    oos_consistent_pnl_config: { pnl_weight:0.30, sharpe_weight:0.35, profitable_pct_weight:0.22, consistency_weight:0.13, pnl_norm:28.0, sharpe_norm:1.4, profitable_pct_target:0.68 },
  },
  {
    key: "prop_firm",
    label: "Prop-Firm",
    icon: "🏦",
    desc: "Funded Account / Challenge. Drawdown-Limits dominieren, kein einzelnes Window darf zu viel verlieren.",
    color: "#dc2626",
    hard_constraints_is:  { min_trades:35, min_profit_factor:1.35, max_drawdown_pct:8.0, min_win_rate:0.28 },
    hard_constraints_oos: { min_window_pnl_usd:0, max_single_window_loss_usd:800, max_rolling_3window_loss_usd:2000, max_total_drawdown_pct:6, min_total_oos_pnl_usd:500, min_profitable_windows_pct:68, min_reliable_windows_pct:65 },
    oos_consistent_pnl_config: { pnl_weight:0.25, sharpe_weight:0.35, profitable_pct_weight:0.22, consistency_weight:0.18, pnl_norm:20.0, sharpe_norm:1.2, profitable_pct_target:0.68 },
  },
];

// Badge showing which optimizer modes / pipeline steps use a parameter
const MODE_BADGES = {
  IS_OPT:  { label:"IS-Optimizer", color:"#2563eb",  bg:"rgba(37,99,235,0.09)"  },
  WF:      { label:"Walk-Forward", color:"#16a34a",  bg:"rgba(22,163,74,0.09)"  },
  OOS_OPT: { label:"WF-Objective", color:"#0891b2",  bg:"rgba(8,145,178,0.09)"  },
  SCORE:   { label:"Score",        color:"#7c3aed",  bg:"rgba(124,58,237,0.09)" },
};
function MBadge({ type }) {
  const b = MODE_BADGES[type];
  return <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:3,
    color:b.color, background:b.bg, border:`1px solid ${b.color}33`, whiteSpace:"nowrap" }}>{b.label}</span>;
}

function GoalsEditor({ goals, onChange }) {
  // Guard: if goals is null/undefined, initialize with defaults on first render
  const safeGoals = goals || GOALS_DEFAULTS;
  const cfg  = safeGoals?.oos_consistent_pnl_config || {};
  const hcIS = safeGoals?.hard_constraints_is || {};
  const hcOOS= safeGoals?.hard_constraints_oos || {};
  const [activeTemplate, setActiveTemplate] = useState(null);
  const patchCfg  = patch => onChange({ ...safeGoals, oos_consistent_pnl_config: { ...cfg,  ...patch } });
  const patchIS   = patch => onChange({ ...safeGoals, hard_constraints_is:        { ...hcIS, ...patch } });
  const patchOOS  = patch => onChange({ ...safeGoals, hard_constraints_oos:       { ...hcOOS,...patch } });
  const pnlW   = cfg.pnl_weight            ?? 0.35;
  const sharpeW= cfg.sharpe_weight         ?? 0.30;
  const ppW    = cfg.profitable_pct_weight ?? 0.20;
  const consW  = cfg.consistency_weight    ?? 0.15;
  const weightSum = +(pnlW + sharpeW + ppW + consW).toFixed(3);
  const weightOk  = Math.abs(weightSum - 1.0) < 0.01;

  const applyTemplate = (tpl) => {
    setActiveTemplate(tpl.key);
    const base = goals || {};
    onChange({
      ...base,
      hard_constraints_is:  { ...(base.hard_constraints_is  || {}), ...tpl.hard_constraints_is  },
      hard_constraints_oos: { ...(base.hard_constraints_oos || {}), ...tpl.hard_constraints_oos },
      oos_consistent_pnl_config: { ...(base.oos_consistent_pnl_config || {}), ...tpl.oos_consistent_pnl_config },
    });
  };

  const GRow = ({ label, desc, range, value, onChange: onC, step=0.05, min=0, max=100, pct=false, unit="", badges=[] }) => (
    <div style={{ padding:"10px 0", borderBottom:`1px solid ${theme.divider}` }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, fontWeight:700, color:theme.text }}>{label}</span>
            {badges.map(b => <MBadge key={b} type={b} />)}
          </div>
          {desc  && <div style={{ fontSize:12, color:theme.text2, lineHeight:1.5, marginBottom:2 }}>{desc}</div>}
          {range && <div style={{ fontSize:11, color:theme.text3, fontFamily:fonts.mono }}>Empfohlen: {range}</div>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0, paddingTop:2 }}>
          <input type="number"
            value={pct ? +(value*100).toFixed(1) : value}
            step={pct ? step*100 : step} min={pct ? min*100 : min} max={pct ? max*100 : max}
            onChange={e => onC(pct ? +e.target.value/100 : +e.target.value)}
            style={{ width:82, padding:"5px 9px", borderRadius:6, border:`1.5px solid ${theme.border}`,
                     fontSize:14, fontFamily:fonts.mono, fontWeight:700,
                     background:theme.white, color:theme.text, textAlign:"right" }} />
          <span style={{ fontSize:12, color:theme.text3, minWidth:20 }}>{pct ? "%" : unit}</span>
        </div>
      </div>
    </div>
  );

  const SectionHead = ({ t, color, desc }) => (
    <div style={{ marginTop:22, marginBottom:6, paddingBottom:6, borderBottom:`2px solid ${color||theme.border}` }}>
      <div style={{ fontSize:11, fontWeight:800, letterSpacing:"1.4px", textTransform:"uppercase", color }}>{t}</div>
      {desc && <div style={{ fontSize:12, color:theme.text3, marginTop:3 }}>{desc}</div>}
    </div>
  );

  const handleReset = () => {
    setActiveTemplate("standard");
    onChange({ ...safeGoals, ...GOALS_DEFAULTS,
      oos_consistent_pnl_config: { ...cfg, ...GOALS_DEFAULTS.oos_consistent_pnl_config },
      hard_constraints_is:  { ...hcIS,  ...GOALS_DEFAULTS.hard_constraints_is  },
      hard_constraints_oos: { ...hcOOS, ...GOALS_DEFAULTS.hard_constraints_oos },
    });
  };

  return (
    <div style={{ fontSize:13 }}>

      {/* ── Templates ─────────────────────────────────────────────── */}
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:800, letterSpacing:"1.3px", textTransform:"uppercase", color:theme.text3, marginBottom:10 }}>Preset Templates</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:8 }}>
          {GOALS_TEMPLATES.map(tpl => {
            const active = activeTemplate === tpl.key;
            return (
              <button key={tpl.key} onClick={() => applyTemplate(tpl)}
                title={tpl.desc}
                style={{ padding:"10px 12px", borderRadius:8, cursor:"pointer", textAlign:"left",
                         border:`2px solid ${active ? tpl.color : theme.border}`,
                         background: active ? `${tpl.color}12` : theme.white,
                         transition:"all 0.15s" }}>
                <div style={{ fontSize:16, marginBottom:4 }}>{tpl.icon}</div>
                <div style={{ fontSize:12, fontWeight:700, color: active ? tpl.color : theme.text }}>{tpl.label}</div>
                <div style={{ fontSize:10, color:theme.text3, marginTop:3, lineHeight:1.4 }}>{tpl.desc.split(".")[0]}.</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Score Formula Banner ───────────────────────────────────── */}
      <div style={{ background:theme.blueLight, border:`1px solid ${theme.border}`,
                    borderRadius:10, padding:"12px 16px", marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
          <div style={{ fontSize:13, fontWeight:800, color:theme.blueText }}>Aktive Score-Formel</div>
          <button onClick={handleReset}
            style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${theme.border}`,
                     background:theme.white, color:theme.text2, cursor:"pointer",
                     fontSize:12, fontWeight:600 }}>
            ↺ Auf Defaults zurücksetzen
          </button>
        </div>
        <div style={{ fontFamily:fonts.mono, fontSize:12, color:theme.blueText, lineHeight:1.8 }}>
          Score = (<b>PnL×{pnlW}</b> + <b>Sharpe×{sharpeW}</b> + <b>ProfPct×{ppW}</b> + <b>Consist.×{consW}</b>)
          &nbsp;× Reliability × Bootstrap-Bonus
        </div>
        <div style={{ fontSize:11, color:theme.text3, marginTop:5, lineHeight:1.6 }}>
          PnL-Score: avg_pnl ÷ ${cfg.pnl_norm??30}/Trade → 1.0 &nbsp;|&nbsp;
          Sharpe-Score: Sharpe ÷ {cfg.sharpe_norm??1.5} → 1.0
          {!weightOk && <span style={{ color:theme.redText, fontWeight:700, marginLeft:8 }}>
            ⚠ Weights = {weightSum} (muss 1.0 sein)
          </span>}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 40px" }}>

        {/* ── LEFT ── */}
        <div>
          <SectionHead t="IS Hard Constraints" color={theme.amberText}
            desc="Disqualifiziert Trials sofort — kein WF-Test wenn nicht bestanden." />
          <GRow label="Min Trades" badges={["IS_OPT","OOS_OPT","WF"]}
            desc="Mindestanzahl Trades im IS-Backtest. Zu wenig = statistisch bedeutungslos."
            range="25–50 (bei wenig Signalen: 20)"
            value={hcIS.min_trades??30} step={5} min={10} max={200}
            onChange={v=>patchIS({min_trades:v})} />
          <GRow label="Min Profit Factor" badges={["IS_OPT","OOS_OPT","WF"]}
            desc="Brutto-Gewinn ÷ Brutto-Verlust. 1.0 = Breakeven. Zu niedrig → fragile Trials bestehen, die live kippen."
            range="1.2–1.5 (unter 1.1 bedeutungslos)"
            value={hcIS.min_profit_factor??1.25} step={0.05} min={1} max={3}
            onChange={v=>patchIS({min_profit_factor:v})} />
          <GRow label="Max Drawdown %" unit="%" badges={["IS_OPT","OOS_OPT","WF"]}
            desc="Maximaler IS-Drawdown. Hohe Werte erlauben Strategien die IS gut aussehen aber live instabil sind."
            range="10–20% (konservativ: 12%)"
            value={hcIS.max_drawdown_pct??15} step={1} min={5} max={40}
            onChange={v=>patchIS({max_drawdown_pct:v})} />
          <GRow label="Min Win Rate" pct badges={["IS_OPT","OOS_OPT","WF"]}
            desc="Mindest-Trefferquote. Für High-RR-Strategien (RR 4+) realistisch 25–35%."
            range="20–35% (SMC/ICT: 25–30%)"
            value={hcIS.min_win_rate??0.25} step={0.01} min={0.1} max={0.6}
            onChange={v=>patchIS({min_win_rate:v})} />
          <GRow label="Min Sharpe" badges={["IS_OPT","OOS_OPT","WF"]}
            desc="Mindest-Sharpe (IS). Trials darunter werden disqualifiziert."
            range="0.0–1.5 (Aggressive: 0.3, Conservative: 1.5)"
            value={hcIS.min_sharpe??0.3} step={0.1} min={0} max={3}
            onChange={v=>patchIS({min_sharpe:v})} />

          <SectionHead t="Score Weights" color={theme.blueText}
            desc="Gewichtung der vier Score-Komponenten. Müssen in Summe 1.0 ergeben." />
          <GRow label="PnL Weight" badges={["IS_OPT","OOS_OPT","SCORE"]}
            desc="Gewicht des avg PnL/Trade. Saturiert bei pnl_norm — differenziert bei MGC kaum wenn alle Trades > pnl_norm."
            range="0.25–0.40"
            value={pnlW} step={0.05} min={0} max={1}
            onChange={v=>patchCfg({pnl_weight:v})} />
          <GRow label="Sharpe Weight" badges={["IS_OPT","OOS_OPT","SCORE"]}
            desc="Gewicht des Sharpe Ratios. Belohnt konstanten Return mit geringer Volatilität — wichtigster Qualitätsindikator."
            range="0.25–0.35 (empfohlen: 0.30)"
            value={sharpeW} step={0.05} min={0} max={1}
            onChange={v=>patchCfg({sharpe_weight:v})} />
          <GRow label="Profitable Pct Weight" badges={["IS_OPT","OOS_OPT","SCORE"]}
            desc="Anteil profitabler IS-Zeitbuckets (von 6). Verhindert dass ein einzelnes gutes Quartal alles rettet."
            range="0.15–0.25"
            value={ppW} step={0.05} min={0} max={1}
            onChange={v=>patchCfg({profitable_pct_weight:v})} />
          <GRow label="Consistency Weight" badges={["IS_OPT","OOS_OPT","SCORE"]}
            desc="Belohnt gleichmäßige PnL-Verteilung. Niedriger Variation Coefficient = hoher Score."
            range="0.10–0.20"
            value={consW} step={0.05} min={0} max={1}
            onChange={v=>patchCfg({consistency_weight:v})} />
        </div>

        {/* ── RIGHT ── */}
        <div>
          <SectionHead t="OOS Hard Constraints" color={theme.greenText}
            desc="Walk-Forward Out-of-Sample Filter — entscheidend für Live-Tauglichkeit." />
          <GRow label="Min Total OOS PnL" unit="$" badges={["WF"]}
            desc="Summe aller OOS-Window PnLs. 0 = nur Breakeven nötig — zu wenig für echten Qualitätsnachweis."
            range="500–2000$ (je nach Kapital)"
            value={hcOOS.min_total_oos_pnl_usd??750} step={250} min={-2000} max={10000}
            onChange={v=>patchOOS({min_total_oos_pnl_usd:v})} />
          <GRow label="Max Single Window Loss" unit="$" badges={["WF"]}
            desc="Maximaler Verlust in einem einzelnen OOS-Window. Kontrolliert Tail-Risk einzelner Marktphasen."
            range="1000–3000$ (Faustregel: 2–3% Kapital)"
            value={hcOOS.max_single_window_loss_usd??2000} step={250} min={500} max={10000}
            onChange={v=>patchOOS({max_single_window_loss_usd:v})} />
          <GRow label="Max Rolling 3-Window Loss" unit="$" badges={["WF"]}
            desc="Maximaler kumulierter Verlust über 3 aufeinanderfolgende OOS-Windows. Verhindert anhaltende Drawdown-Phasen."
            range="3000–8000$ (Faustregel: 5–8% Kapital)"
            value={hcOOS.max_rolling_3window_loss_usd??5000} step={500} min={1000} max={20000}
            onChange={v=>patchOOS({max_rolling_3window_loss_usd:v})} />
          <GRow label="Max Total OOS DD" unit="%" badges={["WF"]}
            desc="Maximaler Drawdown über den gesamten OOS-Zeitraum. Direkter Indikator für Live-Risiko."
            range="8–15% (konservativ: 10%)"
            value={hcOOS.max_total_drawdown_pct??10} step={1} min={1} max={30}
            onChange={v=>patchOOS({max_total_drawdown_pct:v})} />
          <GRow label="Min Profitable Windows" pct badges={["WF"]}
            desc="Anteil der WF-Windows mit positivem OOS-PnL. 65% = mind. 4 von 6 Windows profitabel."
            range="60–75% (unter 50% = nicht robust)"
            value={hcOOS.min_profitable_windows_pct??65} step={5} min={0} max={100}
            onChange={v=>patchOOS({min_profitable_windows_pct:v})} />
          <GRow label="Min Reliable Windows" pct badges={["WF"]}
            desc="Anteil der Windows mit ausreichend Trades für statistische Aussage."
            range="55–70%"
            value={hcOOS.min_reliable_windows_pct??60} step={5} min={0} max={100}
            onChange={v=>patchOOS({min_reliable_windows_pct:v})} />
          <GRow label="Min Window PnL" unit="$" badges={["WF"]}
            desc="Untergrenze für den PnL eines einzelnen OOS-Windows (negativ erlaubt — toleriert Verlust-Windows bis zu dieser Grenze)."
            range="-2000 bis 0 $"
            value={hcOOS.min_window_pnl_usd??-1500} step={250} min={-10000} max={5000}
            onChange={v=>patchOOS({min_window_pnl_usd:v})} />

          <SectionHead t="Score Normalisierung" color={theme.text3}
            desc="Bei welchem Wert Score-Komponente = 1.0 erreicht wird." />
          <GRow label="PnL Norm ($/Trade)" unit="$" badges={["IS_OPT","OOS_OPT","SCORE"]}
            desc="avg_pnl_per_trade bei dem pnl_score = 1.0. Zu hoch → alle Trials saturieren (keine Differenzierung)."
            range="20–50$ (MGC empfohlen: 30$)"
            value={cfg.pnl_norm??30} step={5} min={5} max={200}
            onChange={v=>patchCfg({pnl_norm:v})} />
          <GRow label="Sharpe Norm" badges={["IS_OPT","OOS_OPT","SCORE"]}
            desc="Sharpe Ratio bei dem sharpe_score = 1.0. Futures-Strategien mit Sharpe > 1.5 gelten als sehr gut."
            range="1.0–2.0 (Futures empfohlen: 1.5)"
            value={cfg.sharpe_norm??1.5} step={0.25} min={0.5} max={5}
            onChange={v=>patchCfg({sharpe_norm:v})} />
          <GRow label="Profitable Pct Target" pct badges={["IS_OPT","OOS_OPT","SCORE"]}
            desc="Anteil profitabler Buckets der als Maximum gilt (Cap). Darüber kein zusätzlicher Score-Bonus."
            range="50–70% (empfohlen: 65%)"
            value={cfg.profitable_pct_target??0.65} step={0.05} min={0.3} max={1}
            onChange={v=>patchCfg({profitable_pct_target:v})} />
        </div>
      </div>

      {safeGoals?.monte_carlo && (
        <div style={{ marginTop:20, padding:"12px 16px", background:theme.tint, borderRadius:8 }}>
          <div style={{ fontSize:11, fontWeight:800, letterSpacing:"1.2px", textTransform:"uppercase", color:theme.text3, marginBottom:8 }}>Monte Carlo</div>
          <GRow label="Permutationen" badges={["IS_OPT"]}
            desc="Anzahl MC-Simulationen für den Deploy-Constraint-Check. Mehr = genauere Tail-Risk-Schätzung."
            range="500–2000 (Standard: 1000)"
            value={safeGoals.monte_carlo.n_permutations??1000} step={500} min={100} max={5000}
            onChange={v=>onChange({...safeGoals, monte_carlo:{...safeGoals.monte_carlo, n_permutations:v}})} />
          {/* C9: Deploy-Constraints editierbar — relevant für Deploy-Readiness.
                Validierung (max_dd_95 < max_dd_99) im Save-Guard (validateForm) + Banner unten. */}
          {(() => {
            const dc = safeGoals.monte_carlo.deploy_constraints || {};
            const setDC = patch => onChange({...safeGoals, monte_carlo:{...safeGoals.monte_carlo, deploy_constraints:{...dc, ...patch}}});
            const d95 = dc.max_dd_95pct_pct, d99 = dc.max_dd_99pct_pct;
            const dcErr = (d95!=null && d99!=null && Number(d99)<=Number(d95));
            return <>
              <GRow label="Deploy max DD (95%)" unit="%" badges={["WF"]}
                desc="MC-Tail: 95.-Perzentil des Max-DD darf diesen Wert nicht überschreiten (Deploy-Gate)."
                range="8–15%"
                value={d95??12} step={1} min={1} max={40}
                onChange={v=>setDC({max_dd_95pct_pct:v})} />
              <GRow label="Deploy max DD (99%)" unit="%" badges={["WF"]}
                desc="MC-Tail: 99.-Perzentil des Max-DD (strenger als 95%). Muss > 95%-Wert sein."
                range="12–20%"
                value={d99??16} step={1} min={1} max={50}
                onChange={v=>setDC({max_dd_99pct_pct:v})} />
              {dcErr && <div style={{ fontSize:11, color:"#dc2626", fontWeight:700, marginTop:2 }}>⚠ max DD (99%) muss &gt; max DD (95%) sein</div>}
            </>;
          })()}
          <GRow label="OOS Consensus Top-N" badges={["WF"]}
            desc="Anzahl Top-Trials, aus denen Apply-Consensus die robuste Parameter-Verteilung bildet."
            range="10–30 (Standard: 20)"
            value={safeGoals.oos_consensus_top_n??20} step={1} min={1} max={100}
            onChange={v=>onChange({...safeGoals, oos_consensus_top_n:v})} />
        </div>
      )}

      {/* ── Scoring-Interna (Advanced — read-only): vollständige Transparenz der
            goals-Unterblöcke, die der Editor oben nicht editierbar abdeckt. Reine
            Anzeige aus form.goals (parseYamlToForm lädt den ganzen Block) — kein
            Schreibpfad, Änderung nur im YAML (Etappe 4 offen). ── */}
      <div style={{ marginTop:22, paddingTop:14, borderTop:`2px solid ${theme.divider}` }}>
        <div style={{ fontSize:12, fontWeight:800, letterSpacing:"1px", textTransform:"uppercase", color:"#6d28d9", marginBottom:4 }}>⚙️ Scoring-Interna (Advanced — read-only)</div>
        <div style={{ fontSize:11, color:theme.text3, marginBottom:12 }}>Tiefe Optimizer-/Validierungs-Parameter. Read-only (zu fragil / Score-Mathematik); Änderung nur direkt im YAML. (oos_consensus_top_n, Deploy-Constraints und min_window_pnl sind oben editierbar.)</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
          <RO label="primary_objective" value={safeGoals.primary_objective}/>
          <RO label="optimizer_profile" value={safeGoals.optimizer_profile}/>
          <RO label="cross_instrument.enabled" value={safeGoals.cross_instrument?.enabled}/>
          <RO label="reliability_multiplier_quality_floor" value={cfg.reliability_multiplier_quality_floor}/>
          <RO label="hc_oos.regime_classifier.trend_price_move_pct" value={hcOOS.regime_classifier?.trend_price_move_pct}/>
        </div>
        {safeGoals.bucket_config && <div style={{marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:800,color:theme.text3,textTransform:"uppercase",marginBottom:6}}>bucket_config (inkl. bootstrap_stability)</div>
          <ROGroup obj={safeGoals.bucket_config}/>
        </div>}
        {safeGoals.synthetic_bootstrap && <div style={{marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:800,color:theme.text3,textTransform:"uppercase",marginBottom:6}}>synthetic_bootstrap</div>
          <ROGroup obj={safeGoals.synthetic_bootstrap}/>
        </div>}
        {safeGoals.bar_bootstrap && <div style={{marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:800,color:theme.text3,textTransform:"uppercase",marginBottom:6}}>bar_bootstrap</div>
          <ROGroup obj={safeGoals.bar_bootstrap}/>
        </div>}
        {safeGoals.monte_carlo && <div style={{marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:800,color:theme.text3,textTransform:"uppercase",marginBottom:6}}>monte_carlo.enabled (deploy_constraints sind oben editierbar)</div>
          <ROGroup obj={{enabled:safeGoals.monte_carlo.enabled}}/>
        </div>}
        {safeGoals.wf_objective_config && <div style={{marginBottom:4}}>
          <div style={{fontSize:10,fontWeight:800,color:theme.text3,textTransform:"uppercase",marginBottom:6}}>wf_objective_config</div>
          <ROGroup obj={safeGoals.wf_objective_config}/>
        </div>}
      </div>

      {/* Legend */}
      <div style={{ marginTop:16, padding:"10px 14px", background:theme.tint, borderRadius:8, display:"flex", gap:16, flexWrap:"wrap", alignItems:"center" }}>
        <span style={{ fontSize:11, fontWeight:700, color:theme.text3 }}>Badge-Legende:</span>
        {Object.entries(MODE_BADGES).map(([k,b]) => (
          <div key={k} style={{ display:"flex", alignItems:"center", gap:6 }}>
            <MBadge type={k} />
            <span style={{ fontSize:11, color:theme.text3 }}>
              {k==="IS_OPT" ? "wirkt im Standard/WF-Obj Optimizer IS-Check" :
               k==="OOS_OPT"? "wirkt beim WF-Objective OOS-Scoring" :
               k==="WF"     ? "wirkt beim Walk-Forward Verdikt" :
                              "fließt in Score-Berechnung ein"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Herkunfts-Badge (Stufe 1): read-only Overlay, ändert NICHTS an Edit/Save ── */
// badgeFor(fld) -> {badge, inferred, why} | null. Wird von ParameterForm gesetzt.
const ProvenanceCtx = createContext(null);
function FieldBadge({ info }){
  if(!info) return null;
  const m = BADGES[info.badge]; if(!m) return null;
  return <span title={`${m.label}${info.inferred?" · inferiert (kein Manifest — Schätzung)":""}\n${info.why}`}
    style={{ fontSize:8.5, fontWeight:800, padding:"1px 5px", borderRadius:5, marginLeft:6,
      background:m.bg, color:m.fg, whiteSpace:"nowrap", verticalAlign:"middle",
      border: info.inferred ? `1px dashed ${m.fg}` : "1px solid transparent" }}>
    {m.label}{info.inferred?" ~":""}
  </span>;
}

/* ── Primitives ──────────────────────────────── */
// F: optionales `fld` → Herkunfts-Badge (Stufe 1) + Seed-Rest-Markierung (Stufe 2A),
// read-only Overlay. Ändert NICHTS an Wert/Edit/Save.
function F({label,children,span,fld}){
  const prov = useContext(ProvenanceCtx);
  const info = (fld && prov) ? prov.badgeFor(fld) : null;
  const seedRest = !!(fld && prov && prov.seedRestFor && prov.seedRestFor(fld));
  return <div style={{gridColumn:span?`span ${span}`:undefined, opacity: seedRest ? 0.72 : 1}}>
    <label style={{...lbl,fontSize:10, fontStyle: seedRest ? "italic" : "normal"}}>{label}{info && <FieldBadge info={info}/>}
      {seedRest && <span title="Noch Seed-Rest — der Optimizer ersetzt diesen Wert; erst Optimieren→Apply schreibt den instrument-eigenen Wert. (Onboarding setzt nur Suchräume, §0.)"
        style={{ fontSize:8.5, fontWeight:800, padding:"1px 5px", borderRadius:5, marginLeft:6, background:"rgba(217,119,6,0.14)", color:"#b45309", border:"1px dashed #b45309", whiteSpace:"nowrap" }}>↻ Seed-Rest → Apply</span>}
    </label>{children}
  </div>;
}
// ── RO: read-only-Feld (Builder-Transparenz, Etappe 1) ───────────────────────
// Label + Wert + Edit-Status-Badge + Tooltip. KEIN Input, KEIN Schreibpfad — reine
// Anzeige. status: "design" (🔒 §0-gelockt) | "advanced" (⚙️ nur YAML). Ohne status
// wird über STRUCTURE_MODE_FIELDS klassifiziert (Single-Source) → "design", sonst
// "advanced".
const RO_STATUS = {
  design:   { label:"🔒 Design-fixiert",      bg:"rgba(100,116,139,0.12)", fg:"#475569", tip:"Struktur/Modus — nie optimiert (§0). Read-only im Builder." },
  advanced: { label:"⚙️ Advanced (nur YAML)", bg:"rgba(124,58,237,0.10)",  fg:"#6d28d9", tip:"Tiefe Optimizer-/Lifecycle-Interna — Änderung nur direkt im YAML." },
};
function RO({ label, value, status, fld, note }){
  const resolved = status || (fld && STRUCTURE_MODE_FIELDS.has(fld) ? "design" : "advanced");
  const m = RO_STATUS[resolved] || RO_STATUS.advanced;
  const disp = (value === undefined || value === null || value === "")
    ? "—"
    : (typeof value === "object" ? JSON.stringify(value) : String(value));
  return <div style={{ opacity:0.9 }}>
    <label style={{ ...lbl, fontSize:10, display:"flex", alignItems:"center", flexWrap:"wrap", gap:4 }}>
      <span>{label}</span>
      <span title={m.tip + (note ? `\n${note}` : "")} style={{ fontSize:8, fontWeight:800, padding:"1px 5px", borderRadius:5, background:m.bg, color:m.fg, whiteSpace:"nowrap" }}>{m.label}</span>
    </label>
    <div title={disp} style={{ ...inp, background:theme.tint, color:theme.text2, fontFamily:fonts.mono,
      fontSize:12, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", cursor:"default" }}>{disp}</div>
  </div>;
}
// Flacht ein verschachteltes Objekt in [pfad, wert]-Blätter (für vollständige
// read-only-Anzeige von goals-Unterblöcken — driftfrei, zeigt jedes Leaf).
function _flattenLeaves(obj, prefix=""){
  const out = [];
  for (const [k,v] of Object.entries(obj || {})){
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) out.push(..._flattenLeaves(v, key));
    else out.push([key, v]);
  }
  return out;
}
// ROGroup: rendert alle Leaves eines Objekts als RO-Grid (status=advanced).
function ROGroup({ obj, status="advanced" }){
  const leaves = _flattenLeaves(obj);
  if (!leaves.length) return <div style={{ fontSize:11, color:theme.text4, fontStyle:"italic" }}>— nicht gesetzt —</div>;
  return <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
    {leaves.map(([k,v]) => <RO key={k} label={k} value={v} status={status}/>)}
  </div>;
}
function Tog({checked,onChange,label}){
  return <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={()=>onChange(!checked)}>
    <div style={{width:36,height:20,borderRadius:10,background:checked?theme.green:theme.border,transition:"background 0.2s",position:"relative",flexShrink:0}}>
      <div style={{width:16,height:16,borderRadius:8,background:"#fff",position:"absolute",top:2,left:checked?18:2,transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.15)"}}/>
    </div>
    {label && <span style={{fontSize:12,fontWeight:600,color:theme.text2}}>{label}</span>}
  </div>;
}
function Section({ title, icon, desc, children, defaultOpen=false, summary=null, color=null }) {
  const [open, setOpen] = useState(defaultOpen);
  const accent = color || theme.text3;
  return (
    <div style={{ marginBottom:8, borderRadius:10, border:`1px solid ${open ? accent+"40" : theme.divider}`,
                  background:theme.white, overflow:"hidden", transition:"border-color 0.2s" }}>
      <button onClick={() => setOpen(!open)}
        style={{ width:"100%", padding:"12px 16px", background:"none", border:"none", cursor:"pointer",
                 display:"flex", alignItems:"center", gap:10, textAlign:"left" }}>
        <span style={{ fontSize:18, flexShrink:0 }}>{icon}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:open?accent:theme.text, display:"flex", alignItems:"center", gap:8 }}>
            {title}
          </div>
          {/* Preview chips shown when collapsed */}
          {!open && summary && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:4 }}>
              {summary.map((chip,i) => chip && (
                <span key={i} style={{ fontSize:10, padding:"1px 7px", borderRadius:12,
                  background:chip.color ? chip.color+"15" : theme.tint,
                  color:chip.color || theme.text3, fontFamily:fonts.mono, fontWeight:600, whiteSpace:"nowrap" }}>
                  {chip.label}
                </span>
              ))}
            </div>
          )}
          {open && desc && <div style={{ fontSize:11, color:theme.text3, marginTop:2 }}>{desc}</div>}
        </div>
        <span style={{ fontSize:14, color:theme.text3, transform:open?"rotate(180deg)":"rotate(0)", transition:"transform 0.2s", flexShrink:0 }}>▾</span>
      </button>
      {open && <div style={{ padding:"0 16px 16px", borderTop:`1px solid ${theme.divider}` }}>{children}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SHARED PARAMETER FORM
   Used in both Wizard (Step 2) and Editor mode
   ═══════════════════════════════════════════════════ */
function ParameterForm({ form, sf, showInstrument=true, artifactStatus=null, lifecycle=null, onChange }) {
  const n = (v, d=2) => v != null ? Number(v).toFixed(d) : "—";
  // Stufe 2A: needs_optimize-Zustand (gleiche Bedingung wie die Backtest-Warnung,
  // 7c6260a) → die festen Tunable-Felder sind Seed-Reste, die Apply später ersetzt.
  const APPLIED_STATES = ["optimized", "validated", "walkforwarded", "cross_validated"];
  const needsOptimize = artifactStatus?.meta === "needs_optimize" && !APPLIED_STATES.includes(artifactStatus?.pipeline);
  // Herkunfts-Badges (Stufe 1, read-only Overlay): Seed-Baseline + Optimizer-Tunables
  // einmalig laden (driftfreie Backend-Quellen, KEIN Frontend-Hardcode). badgeFor erst
  // aktiv, wenn die Seed-Baseline da ist (sonst kein Badge statt falsches "manuell").
  const { data: seedRaw } = useApi("/onboard/seed-baseline", 0);
  const { data: tunRaw } = useApi("/optimizer/tunables", 0);
  const seedForm = useMemo(() => (ok(seedRaw) && seedRaw.cfg) ? parseYamlToForm(seedRaw.cfg) : null, [seedRaw]);
  const seedRanges = (ok(seedRaw) && seedRaw.ranges) || {};
  const tunableKeys = useMemo(() => new Set(((ok(tunRaw) && tunRaw.tunables) || []).map(t => t.range_key)), [tunRaw]);
  const prov = useMemo(() => ({
    badgeFor: (fld) => seedForm ? classifyFormField(fld, form, seedForm) : null,
    rangeBadgeFor: (family, kind) => seedForm ? classifyRangeFamily(family, kind, form.ranges || {}, seedRanges, tunableKeys) : null,
    // Stufe 2A: ist DIESES feste Feld ein Seed-Rest, der durch Apply ersetzt wird?
    seedRestFor: (fld) => needsOptimize && TUNABLE_FIXED_FIELDS.has(fld),
  }), [form, seedForm, seedRanges, tunableKeys, needsOptimize]);
  return <ProvenanceCtx.Provider value={prov}>
  {/* Herkunfts-Legende (read-only Overlay) — nur wenn Seed-Baseline geladen */}
  {seedForm && <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:8, marginBottom:10, padding:"7px 11px", borderRadius:8, background:theme.tint, border:`1px solid ${theme.divider}`, fontSize:11, color:theme.text3 }}>
    <b style={{ color:theme.text2 }}>Feld-Herkunft:</b>
    {Object.entries(BADGES).map(([k,m]) => (
      <span key={k} style={{ fontSize:9.5, fontWeight:800, padding:"1px 6px", borderRadius:5, background:m.bg, color:m.fg }}>{m.label}</span>
    ))}
    <span style={{ marginLeft:"auto", fontStyle:"italic" }}>„~" + gestrichelt = inferiert (kein Manifest im Editor → Schätzung; „Manuell"/„Abgeleitet" nicht sicher von Optimizer-Apply unterscheidbar)</span>
  </div>}
  {/* Stufe 2A: Seed-Rest-Hinweis nur bei needs_optimize-Artefakt */}
  {seedForm && needsOptimize && <div style={{ marginBottom:10, padding:"8px 11px", borderRadius:8, background:"rgba(217,119,6,0.10)", border:`1px solid #d97706`, fontSize:11.5, color:"#b45309", lineHeight:1.5 }}>
    <b>↻ Seed-Rest</b>-markierte Felder tragen noch <b>Seed-Werte</b> (nicht instrument-eigen). Der Optimizer ersetzt sie; erst <b>Optimieren → Apply</b> schreibt die instrument-eigenen Werte. Onboarding steckt nur Suchräume ab (§0). <span style={{ opacity:0.8 }}>(<code>_meta.status:needs_optimize</code>)</span>
  </div>}
  {/* ── Identität & Lifecycle (Etappe 3, read-only): aus der ROHEN cfg, nicht aus
        dem Form. Nur im Editor-Modus (lifecycle gesetzt). Reine Anzeige. ── */}
  {lifecycle && <Section title="Identität & Lifecycle" icon="🪪" color="#0891b2"
    summary={[
      lifecycle.strategy_family && {label:lifecycle.strategy_family},
      lifecycle._meta?.status && {label:`_meta: ${lifecycle._meta.status}`, color:"#0891b2"},
      lifecycle.pipeline_status && {label:`pipeline: ${lifecycle.pipeline_status}`},
    ]}>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:12}}>
      <RO label="strategy_family" value={lifecycle.strategy_family}/>
      <RO label="created_at" value={lifecycle.created_at}/>
      <RO label="updated_at" value={lifecycle.updated_at}/>
      <RO label="_meta.purpose" value={lifecycle._meta?.purpose}/>
      <RO label="_meta.status" value={lifecycle._meta?.status}/>
      <RO label="_meta.asset" value={lifecycle._meta?.asset}/>
      <RO label="_meta.parent" value={lifecycle._meta?.parent}/>
      <RO label="_meta.description" value={lifecycle._meta?.description}/>
      <RO label="pipeline.status" value={lifecycle.pipeline_status}/>
      <RO label="pipeline.history (#)" value={lifecycle.pipeline_history_n}/>
      <RO label="broker.type" value={lifecycle.broker_type} fld="inst"/>
      <RO label="broker.account_type" value={lifecycle.broker_account_type}/>
      <RO label="broker.paper_account" value={lifecycle.broker_paper_account}/>
      <RO label="instrument.sec_type" value={lifecycle.instrument_sec_type} status="design" note="aus Instrument-Auswahl"/>
      <RO label="instrument.currency" value={lifecycle.instrument_currency} status="design" note="aus Instrument-Auswahl"/>
    </div>
  </Section>}
  {/* 2-column grid layout */}
  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>

    {/* ── LEFT COLUMN ── */}
    <div>

    {showInstrument && <Section title="Instrument & Timeframe" icon="🎯" defaultOpen={true} color="#2563eb"
      summary={[
        {label:form.inst, color:"#2563eb"},
        {label:`${form.entry_tf}/${form.ctx_tf}/${form.regime_tf}`},
        {label:form.status==="live"?"🔥 Live":form.status==="paper"?"📝 Paper":"🧪 Test",
         color:form.status==="live"?"#dc2626":form.status==="paper"?"#2563eb":"#6b7280"},
      ]}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginTop:12, marginBottom:10 }}>
        <F label="Instrument" fld="inst"><select value={form.inst} onChange={e=>sf("inst",e.target.value)} style={{...sel,fontSize:13}}>{ALL_INSTRUMENTS.map(i=><option key={i}>{i}</option>)}</select></F>
        <F label="Entry TF" fld="entry_tf"><select value={form.entry_tf} onChange={e=>sf("entry_tf",e.target.value)} style={sel}>{TFS.map(t=><option key={t}>{t}</option>)}</select></F>
        <F label="Context TF" fld="ctx_tf"><select value={form.ctx_tf} onChange={e=>sf("ctx_tf",e.target.value)} style={sel}>{TFS.map(t=><option key={t}>{t}</option>)}</select></F>
        <F label="Regime TF" fld="regime_tf"><select value={form.regime_tf} onChange={e=>sf("regime_tf",e.target.value)} style={sel}>{TFS.map(t=><option key={t}>{t}</option>)}</select></F>
        <F label="Status" fld="status"><select value={form.status||"test"} onChange={e=>sf("status",e.target.value)} style={sel}>
          <option value="test">🧪 Test</option><option value="paper">📝 Paper</option>
          <option value="live">🔥 Live</option><option value="archived">📦 Archiviert</option>
        </select></F>
        <F label="Interner Name" fld="name"><input value={form.name} onChange={e=>sf("name",e.target.value)} placeholder={`SMC_MTF_${form.inst}`} style={{...inp,fontSize:12}}/></F>
      </div>
      {INST_META[form.inst] && <div style={{padding:"6px 10px",borderRadius:7,background:theme.tint,fontSize:11,fontFamily:fonts.mono,color:theme.text3}}>
        tick={INST_META[form.inst].tick} · PV={INST_META[form.inst].pv} · margin=${INST_META[form.inst].margin} · {INST_META[form.inst].exchange}
      </div>}
    </Section>}

    <Section title="Risk & Position Sizing" icon="⚠️" color="#f59e0b" defaultOpen={true}
      summary={[
        {label:`Risk ${form.risk_pct}%`, color:"#f59e0b"},
        {label:`$${Number(form.account).toLocaleString()}`},
        {label:`Max ${form.max_contracts} Kontrakte`},
        {label:`Cooldown ${form.cooldown} Bars`},
        form.sl_streak_enabled && {label:"SL Streak 🛡", color:"#059669"},
      ]}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginTop:12,marginBottom:10}}>
        <F label="Risk %" fld="risk_pct"><input type="number" step="0.1" value={form.risk_pct} onChange={e=>sf("risk_pct",Number(e.target.value))} style={{...inp,fontSize:13}}/></F>
        <F label="Account $" fld="account"><input type="number" step="1000" value={form.account} onChange={e=>sf("account",Number(e.target.value))} style={{...inp,fontSize:13}}/></F>
        <F label="Max Contracts" fld="max_contracts"><input type="number" step="1" value={form.max_contracts??15} onChange={e=>sf("max_contracts",Number(e.target.value))} style={inp}/></F>
        <F label="Min Trade Qty" fld="min_trade_qty"><input type="number" step="1" value={form.min_trade_qty??1} onChange={e=>sf("min_trade_qty",Number(e.target.value))} style={inp}/></F>
        <F label="Max Daily Trades" fld="max_daily"><input type="number" value={form.max_daily} onChange={e=>sf("max_daily",Number(e.target.value))} style={inp} title="0 = unbegrenzt"/></F>
        <F label="Cooldown Bars" fld="cooldown"><input type="number" value={form.cooldown} onChange={e=>sf("cooldown",Number(e.target.value))} style={inp}/></F>
        <F label="Max Bars/Trade" fld="max_bars_in_trade"><input type="number" value={form.max_bars_in_trade??2000} onChange={e=>sf("max_bars_in_trade",Number(e.target.value))} style={inp}/></F>
        <F label="Min SL Points" fld="min_sl_points"><input type="number" step="1" value={form.min_sl_points??5} onChange={e=>sf("min_sl_points",Number(e.target.value))} style={inp}/></F>
        <F label="Commission $" fld="commission_per_contract"><input type="number" step="0.01" value={form.commission_per_contract??0.25} onChange={e=>sf("commission_per_contract",Number(e.target.value))} style={inp}/></F>
      </div>
      {/* B-1 Slippage: Checkbox-als-View — Zustand wird aus spread_ticks abgeleitet
          (0 = aus, >0 = an); es wird NUR die eine Zahl geschrieben, kein zweites
          slippage_enabled-Feld. Slippage ist in der Engine ein reiner Reporting-
          Kosten-Abzug (kein Fill-Shift), siehe B-1 Engine-Verdrahtung. */}
      <div style={{padding:"10px 12px",borderRadius:8,background:theme.tint,border:`1px solid ${theme.divider}`,marginBottom:10}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:(form.spread_ticks??0)>0?10:0}}>
          <Tog checked={(form.spread_ticks??0)>0} onChange={v=>sf("spread_ticks",v?1:0)}/>
          <div>
            <span style={{fontSize:12,fontWeight:700,color:theme.text}}>Slippage simulieren</span>
            <span style={{fontSize:11,color:theme.text3,marginLeft:8}}>0 = rein datengetrieben, ohne Ausführungskosten (Pine-Parität)</span>
          </div>
        </div>
        {(form.spread_ticks??0)>0 && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10}}>
          <F label="Spread Ticks" fld="spread_ticks"><input type="number" step="1" min="0" value={form.spread_ticks??0} onChange={e=>sf("spread_ticks",Number(e.target.value))} style={inp}/></F>
          <div style={{gridColumn:"span 3",alignSelf:"center",fontSize:11,color:theme.text3}}>Round-trip Kosten-Abzug: qty × ticks × tick_size × point_value × 2</div>
        </div>}
      </div>
      <div style={{padding:"10px 12px",borderRadius:8,background:theme.tint,border:`1px solid ${theme.divider}`}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:form.sl_streak_enabled?10:0}}>
          <Tog checked={form.sl_streak_enabled||false} onChange={v=>sf("sl_streak_enabled",v)}/>
          <div>
            <span style={{fontSize:12,fontWeight:700,color:theme.text}}>SL Streak Protection</span>
            <span style={{fontSize:11,color:theme.text3,marginLeft:8}}>Pause nach N Stop-Losses hintereinander</span>
          </div>
        </div>
        {form.sl_streak_enabled && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          <F label="Threshold" fld="sl_streak_threshold"><input type="number" step="1" value={form.sl_streak_threshold??3} onChange={e=>sf("sl_streak_threshold",Number(e.target.value))} style={inp}/></F>
          <F label="Pause Hours" fld="sl_streak_pause_hours"><input type="number" step="1" value={form.sl_streak_pause_hours??24} onChange={e=>sf("sl_streak_pause_hours",Number(e.target.value))} style={inp}/></F>
          <F label="Reset on Winner" fld="sl_streak_reset_on_winner"><Tog checked={form.sl_streak_reset_on_winner!==false} onChange={v=>sf("sl_streak_reset_on_winner",v)} label="ja"/></F>
        </div>}
      </div>
      <div style={{marginTop:12,paddingTop:10,borderTop:`1px solid ${theme.divider}`}}>
        <div style={{fontSize:10,fontWeight:800,color:theme.text3,textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>Feste Risk-Konstanten (read-only)</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
          <RO label="Daily Reset Hour (UTC)" value={22} status="design" note="risk.daily_reset_hour_utc — fix=22"/>
          <RO label="Rolling MFE/MAE Filter" value="off (fix)" status="design" note="risk.regime_filter.rolling_mfe_mae_filter.enabled — fix false"/>
          <RO label="· history_size" value={30} status="design"/>
          <RO label="· trigger/resume RR" value="0.5 / 1.0" status="design"/>
        </div>
      </div>
    </Section>

    <Section title="Structure (SMC)" icon="🔧" color="#7c3aed"
      summary={[
        {label:`ESw${form.entry_swing}`, color:"#7c3aed"},
        {label:`CSw${form.ctx_swing}`},
        {label:form.entry_mode||"both"},
        {label:form.break_confirm||"wick"},
        {label:`Age${form.pattern_age}`},
      ]}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginTop:12}}>
        <F label="CTX Swing" fld="ctx_swing"><input type="number" value={form.ctx_swing} onChange={e=>sf("ctx_swing",Number(e.target.value))} style={inp}/></F>
        <F label="Entry Swing" fld="entry_swing"><input type="number" value={form.entry_swing} onChange={e=>sf("entry_swing",Number(e.target.value))} style={inp}/></F>
        <F label="Pattern Age" fld="pattern_age"><input type="number" value={form.pattern_age} onChange={e=>sf("pattern_age",Number(e.target.value))} style={inp}/></F>
        <F label="CHoCH Age" fld="choch_age"><input type="number" value={form.choch_age} onChange={e=>sf("choch_age",Number(e.target.value))} style={inp}/></F>
        <F label="Break Confirm" fld="break_confirm"><select value={form.break_confirm||"wick"} onChange={e=>sf("break_confirm",e.target.value)} style={sel}>
          <option value="wick">wick</option><option value="close">close</option>
        </select></F>
        <F label="Entry Pattern" fld="entry_mode"><select value={form.entry_mode||"both"} onChange={e=>sf("entry_mode",e.target.value)} style={sel}>
          <option value="both">Single CHoCH</option><option value="bos_in_trend">BoS in Trend</option>
          <option value="choch_then_bos">CHoCH→BoS</option><option value="double_choch">Double CHoCH</option>
        </select></F>
        <F label="Direction" span={2} fld="direction_filter"><select value={form.direction_filter||"both"} onChange={e=>sf("direction_filter",e.target.value)} style={sel}>
          <option value="both">Both</option><option value="long">Long Only</option><option value="short">Short Only</option>
        </select></F>
      </div>
    </Section>

    <Section title="Stop Loss & Take Profit" icon="🎯" color="#dc2626"
      summary={[
        {label:`SL ${n(form.atr_min,2)}–${n(form.atr_max,2)}×ATR`, color:"#dc2626"},
        {label:`TP1 ${n(form.rr1,1)}R`},
        {label:`TP2 ${n(form.rr2,1)}R`},
        {label:`${form.p1}/${form.p2}/${form.p3||0}`},
        {label:`Floor ${form.safety_floor_enabled?"on":"off"}`, color:form.safety_floor_enabled?"#059669":"#6b7280"},
      ]}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:12,marginBottom:8}}>
        <RO label="SL Mode" value={form.sl_mode||"structure_then_atr"} fld="sl_mode"/>
        <RO label="ATR Length" value={form.atr_len||14} status="design" note="ATR-Periode — Engine-Konstante"/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        <F label="ATR Mult Min" fld="atr_min"><input type="number" step="0.1" value={form.atr_min} onChange={e=>sf("atr_min",Number(e.target.value))} style={inp}/></F>
        <F label="ATR Mult Max" fld="atr_max"><input type="number" step="0.1" value={form.atr_max} onChange={e=>sf("atr_max",Number(e.target.value))} style={inp}/></F>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <Tog checked={form.safety_floor_enabled} onChange={v=>sf("safety_floor_enabled",v)}/>
        <span style={{fontSize:12,fontWeight:600,color:theme.text}}>Safety Floor</span>
        {form.safety_floor_enabled && <>
          <F label="Floor ATR" style={{flex:1}} fld="safety_floor_atr"><input type="number" step="0.1" value={form.safety_floor_atr} onChange={e=>sf("safety_floor_atr",Number(e.target.value))} style={{...inp,width:80}}/></F>
          <F label="Floor Ticks" fld="safety_floor_ticks"><input type="number" value={form.safety_floor_ticks} onChange={e=>sf("safety_floor_ticks",Number(e.target.value))} style={{...inp,width:80}}/></F>
        </>}
      </div>
      <div style={{padding:"10px 12px",borderRadius:8,background:theme.tint,border:`1px solid ${theme.divider}`}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:8}}>
          <RO label="TP Mode" value={form.tp_mode||"partial_trail_with_runner"} fld="tp_mode"/>
          <RO label="Num TP Levels" value={form.num_tp_levels??2} fld="num_tp_levels"/>
          <RO label="Runner" value="enabled (fix)" status="design" note="take_profit.runner_enabled — fix true"/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          <F label="TP1 RR" fld="rr1"><input type="number" step="0.25" value={form.rr1} onChange={e=>sf("rr1",Number(e.target.value))} style={inp}/></F>
          <F label="TP2 RR" fld="rr2"><input type="number" step="0.5" value={form.rr2} onChange={e=>sf("rr2",Number(e.target.value))} style={inp}/></F>
          <F label="Partial Preset" fld="partial_preset">
            <select value={form.partial_preset||"20/40/40"} onChange={e=>{
              const v=e.target.value,parts=v.split("/").map(Number);
              sf("partial_preset",v);sf("p1",parts[0]);sf("p2",parts[1]);sf("p3",parts[2]||0);sf("p4",0);
            }} style={sel}>
              <option value="20/40/40">20/40/40 — Runner 40%</option>
              <option value="30/40/30">30/40/30</option>
              <option value="50/50/0">50/50 — kein Runner</option>
              <option value="40/60/0">40/60 — kein Runner</option>
            </select>
          </F>
        </div>
        <div style={{marginTop:6,fontSize:11,color:theme.text3}}>TP1 {form.p1}% · TP2 {form.p2}% · Runner {form.p3||0}%</div>
      </div>
    </Section>

    </div>{/* end left column */}

    {/* ── RIGHT COLUMN ── */}
    <div>

    <Section title="Break Even & Trailing" icon="⚖️" color="#0891b2"
      summary={[
        {label:`BE @${n(form.be_rr,2)}R`, color:"#0891b2"},
        {label:`Trail ${n(form.post_be_trail_atr_mult,2)}×`},
        {label:`Runner ${n(form.runner_trail_atr_mult,2)}×`},
        {label:`Offset ${form.be_offset_ticks||0}T`},
      ]}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginTop:12,marginBottom:10}}>
        <F label="BE" fld="be"><Tog checked={form.be} onChange={v=>sf("be",v)}/></F>
        <F label="BE Trigger RR" fld="be_rr"><input type="number" step="0.25" value={form.be_rr} onChange={e=>sf("be_rr",Number(e.target.value))} style={inp}/></F>
        <F label="BE Offset Ticks" fld="be_offset_ticks"><input type="number" value={form.be_offset_ticks??2} onChange={e=>sf("be_offset_ticks",Number(e.target.value))} style={inp}/></F>
        <div style={{display:"flex",alignItems:"flex-end",paddingBottom:4}}>
          <span style={{fontSize:10,padding:"1px 7px",borderRadius:3,background:"rgba(37,99,235,0.1)",color:theme.blueText,fontFamily:fonts.mono}}>🔒 activate_trail</span>
        </div>
      </div>
      <div style={{padding:"10px 12px",borderRadius:8,background:theme.tint,border:`1px solid ${theme.divider}`}}>
        <div style={{fontSize:11,fontWeight:700,color:theme.text3,textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>Post-BE & Runner Trail (ATR)</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          <F label="Post-BE Trail ATR" fld="post_be_trail_atr_mult"><input type="number" step="0.25" value={form.post_be_trail_atr_mult??2.5} onChange={e=>sf("post_be_trail_atr_mult",Number(e.target.value))} style={inp}/></F>
          <F label="Post-BE Min R" fld="post_be_trail_min_r"><input type="number" step="0.25" value={form.post_be_trail_min_r??0.5} onChange={e=>sf("post_be_trail_min_r",Number(e.target.value))} style={inp}/></F>
          <F label="Runner Trail ATR" fld="runner_trail_atr_mult"><input type="number" step="0.25" value={form.runner_trail_atr_mult??2.5} onChange={e=>sf("runner_trail_atr_mult",Number(e.target.value))} style={inp}/></F>
        </div>
      </div>
    </Section>

    <Section title="Multi-TF Gate" icon="🚦" color="#059669"
      summary={[
        {label:form.mtf_enabled===false?"off":"on", color:form.mtf_enabled===false?"#6b7280":"#059669"},
        form.mtf_enabled!==false && {label:`D-ATR ${n(form.mtf_daily_atr,1)} / ${form.mtf_daily_bars||10}B`},
        form.mtf_enabled!==false && {label:`H1-ATR ${n(form.mtf_h1_atr,1)} / ${form.mtf_h1_bars||12}B`},
      ]}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginTop:12,marginBottom:10}}>
        <Tog checked={form.mtf_enabled!==false} onChange={v=>sf("mtf_enabled",v)}/>
        <span style={{fontSize:12,color:theme.text2}}>Multi-TF Gate (v7 — ersetzt ADX-Regime)</span>
      </div>
      {form.mtf_enabled!==false && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10}}>
        <F label="Daily ATR" fld="mtf_daily_atr"><input type="number" step="0.5" value={form.mtf_daily_atr??3.0} onChange={e=>sf("mtf_daily_atr",Number(e.target.value))} style={inp}/></F>
        <F label="Daily Bars" fld="mtf_daily_bars"><input type="number" step="1" value={form.mtf_daily_bars??10} onChange={e=>sf("mtf_daily_bars",Number(e.target.value))} style={inp}/></F>
        <F label="H1 ATR" fld="mtf_h1_atr"><input type="number" step="0.5" value={form.mtf_h1_atr??3.0} onChange={e=>sf("mtf_h1_atr",Number(e.target.value))} style={inp}/></F>
        <F label="H1 Bars" fld="mtf_h1_bars"><input type="number" step="1" value={form.mtf_h1_bars??12} onChange={e=>sf("mtf_h1_bars",Number(e.target.value))} style={inp}/></F>
      </div>}
      {form.mtf_enabled!==false && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:10}}>
        <RO label="Daily Fractal" value={3} status="design" note="Ghost-Parameter — fix=3"/>
        <RO label="H1 Fractal" value={3} status="design" note="Ghost-Parameter — fix=3"/>
      </div>}
    </Section>

    <Section title="DTR Volatility Filter" icon="📊" color="#7c3aed"
      summary={[
        {label:form.dtr_enabled===false?"off":"on", color:form.dtr_enabled===false?"#6b7280":"#7c3aed"},
        form.dtr_enabled!==false && {label:`P${form.dtr_percentile||99}%`},
        form.dtr_enabled!==false && {label:`${form.dtr_lookback||120} Tage`},
      ]}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginTop:12,marginBottom:10}}>
        <Tog checked={form.dtr_enabled!==false} onChange={v=>sf("dtr_enabled",v)}/>
        <span style={{fontSize:12,color:theme.text2}}>DTR-Filter aktiv</span>
      </div>
      {form.dtr_enabled!==false && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <F label="Lookback Tage" fld="dtr_lookback"><input type="number" step="10" value={form.dtr_lookback??120} onChange={e=>sf("dtr_lookback",Number(e.target.value))} style={inp}/></F>
        <F label="Perzentile %" fld="dtr_percentile"><input type="number" step="1" value={form.dtr_percentile??99.0} onChange={e=>sf("dtr_percentile",Number(e.target.value))} style={inp}/></F>
      </div>}
    </Section>

    <Section title="Filters" icon="🔍" color="#6b7280"
      summary={[
        {label:`FVG: ${form.fvg?"on":"off"}`, color:form.fvg?"#7c3aed":"#6b7280"},
        {label:"EMA: off", color:"#d1d5db"},
        {label:"Regime: Multi-TF Gate", color:"#6b7280"},
      ]}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:12}}>
        <F label="FVG Filter" fld="fvg">
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <Tog checked={form.fvg} onChange={v=>sf("fvg",v)} label={form.fvg?"on":"off"}/>
            <span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:"rgba(124,58,237,0.1)",color:"#7c3aed",fontFamily:fonts.mono}}>Optimizer-Toggle</span>
          </div>
        </F>
        <RO label="EMA Filter" value="off (fix)" fld="ema" note="v7: EMA-Filter immer deaktiviert"/>
      </div>
      <div style={{marginTop:12,paddingTop:10,borderTop:`1px solid ${theme.divider}`}}>
        <div style={{fontSize:10,fontWeight:800,color:theme.text3,textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>Regime-Filter (top-level) — legacy, abgelöst durch Multi-TF-Gate + DTR</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          <RO label="Regime enabled" value={form.regime===true?"on":"off"} fld="regime"/>
          <RO label="Regime Mode" value={form.regime_mode||"either"} fld="regime_mode"/>
          <RO label="ADX Threshold" value={form.adx??15} fld="adx"/>
          <RO label="ADX Length" value={form.adx_length??14} fld="adx_length"/>
          <RO label="ATR Length" value={form.regime_atr_length??14} fld="regime_atr_length"/>
          <RO label="ATR SMA Length" value={form.sma_len??30} fld="sma_len"/>
        </div>
      </div>
    </Section>

    <Section title="Bad Hours" icon="🚫"
      summary={[
        {label:form.bad_hours?.length>0?`${form.bad_hours.length} Stunden gesperrt`:"Keine Sperrstunden", color:form.bad_hours?.length>0?"#dc2626":"#6b7280"},
      ]}>
      <div style={{marginTop:12,fontSize:12,color:theme.text2,marginBottom:8}}>Komma-getrennte UTC-Stunden sperren (z.B. 0, 1, 2, 22, 23)</div>
      <input type="text"
        value={(form.bad_hours||[]).join(", ")}
        onChange={e => sf("bad_hours", e.target.value.split(",").map(s=>parseInt(s.trim())).filter(n=>!isNaN(n)))}
        onKeyDown={e=>e.stopPropagation()} onKeyUp={e=>e.stopPropagation()}
        placeholder="0, 1, 22, 23"
        style={{...inp, width:"100%", fontFamily:fonts.mono}}/>
    </Section>

    </div>{/* end right column */}
  </div>{/* end 2-column grid */}

  {/* Full-width sections below */}

    {/* ── Parameter-Ranges (Optimizer Search Space) ── */}
    <Section title="Parameter-Ranges (Optimizer Search Space)" icon="📐" desc="Definiert den Suchraum des Optimizers. Kein separates Panel im Optimizer mehr — alles hier in der YAML verankert.">
      {(() => {
        // B8: Dirty-Flag — Range geändert an einem schon optimierten Artefakt → Ergebnis veraltet.
        const OPT_STATES = ["optimized","validated","walkforwarded","cross_validated"];
        const rangesDirty = lifecycle?.ranges_at_load && OPT_STATES.includes(lifecycle.pipeline_status)
          && JSON.stringify(form.ranges||{}) !== JSON.stringify(lifecycle.ranges_at_load);
        if (!rangesDirty) return null;
        return <div style={{marginBottom:10, padding:"8px 11px", borderRadius:8, background:"rgba(220,38,38,0.08)", border:"1px solid #dc2626", fontSize:11.5, color:"#b91c1c", lineHeight:1.5}}>
          <b>⚠ Ranges geändert seit der letzten Optimierung</b> (Artefakt-Status: <code>{lifecycle.pipeline_status}</code>). Die best-Params stammen aus dem alten Suchraum und sind jetzt veraltet — <b>neu optimieren</b>, bevor dieses Artefakt gebacktestet/promoted wird.
        </div>;
      })()}
      {(() => {
        const ranges = form.ranges || {};
        const sr = k => v => onChange({ ranges: { ...ranges, [k]: v } });
        const RR = ({ label, kMin, kMax, kStep, min=0, max=10, step=0.1 }) => {
          // B7: Inline-Validierung min<max + Plausi (sofortiges visuelles Feedback;
          // der Save-Guard in validateForm blockt zusätzlich beim Speichern).
          const vMin = ranges[kMin], vMax = ranges[kMax], vStep = kStep ? ranges[kStep] : null;
          const errMsg =
            (vMin != null && Number(vMin) <= 0) ? "Min muss > 0 sein" :
            (vMin != null && vMax != null && Number(vMax) <= Number(vMin)) ? `Max (${vMax}) muss > Min (${vMin}) sein` :
            (vStep != null && Number(vStep) <= 0) ? "Step muss > 0 sein" : null;
          return (
          <div style={{ padding:"8px 0", borderBottom:`1px solid ${theme.divider}` }}>
            <div style={{ fontSize:12, fontWeight:700, color:theme.text, marginBottom:5 }}>{label}
              {prov && <FieldBadge info={prov.rangeBadgeFor(String(kMin).replace(/_min$/,""), "numeric")}/>}</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6 }}>
              {[["Min",kMin],["Max",kMax],kStep&&["Step",kStep]].filter(Boolean).map(([lbl,k]) => (
                <div key={k}>
                  <div style={{ fontSize:10, color:theme.text3, marginBottom:2 }}>{lbl}</div>
                  <input type="number" step={step} value={ranges[k]??""} onChange={e=>sr(k)(Number(e.target.value))}
                    onKeyDown={e=>e.stopPropagation()} onKeyUp={e=>e.stopPropagation()}
                    style={{ width:"100%", ...inp, fontSize:12, ...(errMsg?{borderColor:"#dc2626",background:"rgba(220,38,38,0.06)"}:{}) }}/>
                </div>
              ))}
            </div>
            {errMsg && <div style={{ fontSize:10, color:"#dc2626", marginTop:3, fontWeight:700 }}>⚠ {errMsg}</div>}
          </div>
          );
        };
        const Choices = ({ label, k }) => (
          <div style={{ padding:"8px 0", borderBottom:`1px solid ${theme.divider}` }}>
            <div style={{ fontSize:12, fontWeight:700, color:theme.text, marginBottom:3 }}>{label}
              {prov && <FieldBadge info={prov.rangeBadgeFor(k, "categorical")}/>}</div>
            <input type="text"
              value={Array.isArray(ranges[k]) ? ranges[k].join(", ") : ""}
              onChange={e => sr(k)(e.target.value.split(",").map(s=>s.trim()).filter(Boolean).map(Number).filter(n=>!isNaN(n)))}
              onKeyDown={e=>e.stopPropagation()} onKeyUp={e=>e.stopPropagation()}
              placeholder="z.B. 3, 5, 7, 10, 14"
              style={{ width:"100%", ...inp, fontFamily:fonts.mono, fontSize:12 }}/>
          </div>
        );
        return (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"0 32px" }}>
            <div>
              <div style={{ fontSize:10, fontWeight:800, color:theme.amberText, textTransform:"uppercase", letterSpacing:"1px", marginBottom:6, marginTop:4 }}>Stop Loss & BE & Trail</div>
              <RR label="SL ATR Mult" kMin="sl_atr_mult_min" kMax="sl_atr_mult_max" kStep="sl_atr_mult_step" min={0.5} max={5} step={0.1}/>
              <RR label="ATR Floor Mult" kMin="atr_floor_mult_min" kMax="atr_floor_mult_max" kStep="atr_floor_mult_step" min={0.1} max={2} step={0.1}/>
              <RR label="BE Trigger RR" kMin="be_trigger_rr_min" kMax="be_trigger_rr_max" kStep="be_trigger_rr_step" min={0.25} max={6} step={0.25}/>
              <RR label="Post-BE Trail ATR" kMin="post_be_trail_atr_mult_min" kMax="post_be_trail_atr_mult_max" kStep="post_be_trail_atr_mult_step" min={0.5} max={6} step={0.25}/>
              <RR label="Runner Trail ATR" kMin="runner_trail_atr_mult_min" kMax="runner_trail_atr_mult_max" kStep="runner_trail_atr_mult_step" min={0.5} max={6} step={0.25}/>
            </div>
            <div>
              <div style={{ fontSize:10, fontWeight:800, color:theme.blueText, textTransform:"uppercase", letterSpacing:"1px", marginBottom:6, marginTop:4 }}>Take Profit</div>
              <RR label="TP1 RR" kMin="tp1_rr_min" kMax="tp1_rr_max" kStep="tp1_rr_step" min={1} max={10} step={0.25}/>
              <RR label="TP2 RR" kMin="tp2_rr_min" kMax="tp2_rr_max" kStep="tp2_rr_step" min={2} max={20} step={0.5}/>
              <div style={{ fontSize:10, fontWeight:800, color:theme.blueText, textTransform:"uppercase", letterSpacing:"1px", marginBottom:6, marginTop:12 }}>Partials (Presets){prov && <FieldBadge info={prov.rangeBadgeFor("partial_pcts_presets", "categorical")}/>}</div>
              <div style={{ padding:"8px 0", borderBottom:`1px solid ${theme.divider}` }}>
                <div style={{ fontSize:11, color:theme.text3, marginBottom:4 }}>z.B. 20/40/40, 30/40/30</div>
                <input type="text"
                  value={Array.isArray(ranges.partial_pcts_presets) ? ranges.partial_pcts_presets.join(", ") : ""}
                  onChange={e => sr("partial_pcts_presets")(e.target.value.split(",").map(s=>s.trim()).filter(Boolean))}
                  onKeyDown={e=>e.stopPropagation()} onKeyUp={e=>e.stopPropagation()}
                  style={{ width:"100%", ...inp, fontFamily:fonts.mono, fontSize:12 }}/>
              </div>
            </div>
            <div>
              <div style={{ fontSize:10, fontWeight:800, color:theme.greenText, textTransform:"uppercase", letterSpacing:"1px", marginBottom:6, marginTop:4 }}>Struktur (Choices)</div>
              <Choices label="Entry Swing Length" k="entry_swing_choices"/>
              <Choices label="Context Swing Length" k="context_swing_choices"/>
              <Choices label="CHoCH Max Age" k="context_choch_max_age_choices"/>
            </div>
          </div>
        );
      })()}
    </Section>

    {form.goals && <Section title="Goals (Optimizer Constraints)" icon="🎯" desc="Steuert welche Trials der Optimizer als valid markiert und wie der Score berechnet wird.">
      <GoalsEditor goals={form.goals} onChange={g => onChange({ goals: g })} />
    </Section>}
  </ProvenanceCtx.Provider>;
}

/* ═══════════════════════════════════════════════════
   ONBOARD WIZARD (Phase 3) — Instrument+Quelle → Ziel → Onboarden
   Stufe 1: Step 1+2 UI (kein Lauf). Step 3 Lauf/Polling = Stufe 2.
   Ersetzt den alten CreateWizard (Profil-Relikt). onboard erzeugt die Strategie
   via clone vom Anchor (bestehende Strategie) ODER Seed (von Grund auf).
   ═══════════════════════════════════════════════════ */
const SEED_ANCHOR_FILE = "strategies/templates/_seed_v4_neutral.yaml";

// Schritt 1+2: kompakte Live-Stufen-Anzeige für /onboard/status.stages_progress.
// Reines Reporting — keine Umbau der Läuft/Fertig/Fehler-Logik (Schritt 4 = Stepper).
const ONBOARD_STAGE_DE = {
  data: "Daten", clone: "Klon", measure: "Messung", ranges: "Ranges",
  caps: "Caps", pnl: "PnL-Variante", calib: "Kalibrierung", report: "Report",
};
const onboardStageSummary = (name, s) => {
  if (!s) return "";
  switch (name) {
    case "measure": return `${s.n_entries} Entries · MFE ${s.mfe_R_p50}R`;
    case "ranges":  return `TP1 ${s.tp1_rr_min}–${s.tp1_rr_max} · TP2 ${s.tp2_rr_min}–${s.tp2_rr_max}`;
    case "caps":    return `max ${s.max_contracts} Kontrakte · min Qty ${s.min_trade_qty}`;
    case "report":  return `Grade ${s.grade} · ${s.verdict}`;
    case "calib":   return s.dry_run ? "dry-run" : `run ${s.calmar_run_id}/${s.pnl_run_id}`;
    case "data":    return s.ok ? "OK" : "—";
    default:        return s.schema ? `schema ${s.schema}` : "✓";
  }
};

// T0-ARCH: Abschluss = „Was hat das Onboarding gesetzt"-Screen (Onboarding optimiert
// nicht → kein Grade/PnL, sondern volle Suchraum-Transparenz über alle 12 Optimizer-Params).
const fmtNum = (v, d = 2) => (typeof v === "number" ? v.toFixed(d) : "—");
// mfe_R-Perzentile haben int-Keys; via JSON werden sie zu Strings → beide abfragen.
const pick = (obj, k) => (obj ? (obj[k] ?? obj[String(k)]) : undefined);

// Quelle eines Tunables im Artefakt: "derived" (gemessen) | "seed" (konstant geerbt) |
// "default" (gar nicht gesetzt → Optimizer-Code-Default). Numerics stehen als {key}_min,
// Categoricals als {key}. derivedKeys = die von derive_ranges gesetzten Keys (stages.ranges).
const tunableSource = (t, ar, derivedKeys) => {
  const setKey = t.kind === "numeric" ? `${t.range_key}_min` : t.range_key;
  if (ar[setKey] === undefined || ar[setKey] === null) return "default";
  return derivedKeys.has(setKey) ? "derived" : "seed";
};
const tunableValue = (t, ar) => {
  if (t.kind === "numeric") {
    const lo = ar[`${t.range_key}_min`], hi = ar[`${t.range_key}_max`];
    return lo !== undefined && lo !== null ? `${lo} – ${hi}` : `${t.default.min} – ${t.default.max}`;
  }
  const ch = Array.isArray(ar[t.range_key]) ? ar[t.range_key] : t.default.choices;
  return Array.isArray(ch) ? ch.join(", ") : String(ch);
};
const TUNABLE_SOURCE_BADGE = {
  derived: { txt: "Abgeleitet", bg: "rgba(16,185,129,0.12)", fg: "#047857" },
  seed:    { txt: "Seed-Default (bewusst konstant)", bg: "rgba(100,116,139,0.12)", fg: "#475569" },
  default: { txt: "Optimizer-Default · NICHT vom Onboarding gesetzt", bg: "rgba(217,119,6,0.12)", fg: "#b45309" },
};

// T3-B1: TF-Minuten (Spiegel von onboard_asset._TF_MINUTES) für Client-Hierarchie-Check
// (entry < context < regime). Backend re-validiert hart; das hier ist nur Frühfeedback.
const TF_MIN = { M1:1, M3:3, M5:5, M10:10, M15:15, M30:30, H1:60, H2:120, H4:240, D:1440, W:10080 };
const TF_ENTRY_OPTS = ["M1", "M3", "M5", "M10", "M15", "M30"];
const TF_CONTEXT_OPTS = ["M30", "H1", "H2", "H4"];
const TF_REGIME_OPTS = ["H4", "D", "W"];

function OnboardWizard({ files, onCreated, onOptimize, onCancel }) {
  const [step, setStep] = useState(1);
  const [inst, setInst] = useState(null);
  const [sourceMode, setSourceMode] = useState("scratch");   // "scratch" | "existing"
  const [anchorSym, setAnchorSym] = useState("");
  const [objective, setObjective] = useState("robust");       // "robust" | "pnl"
  // T3-B1/B2/B3: wählbare Lauf-Parameter (Defaults = bisherige Runner-Defaults).
  const [tfEntry, setTfEntry] = useState("M5");
  const [tfContext, setTfContext] = useState("H1");
  const [tfRegime, setTfRegime] = useState("D");
  const [startDate, setStartDate] = useState("2022-01-01");
  const [endDate, setEndDate] = useState("2026-04-01");
  const [launched, setLaunched] = useState(false);            // ein Lauf wurde gestartet
  const [onboarding, setOnboarding] = useState(false);        // pollt /onboard/status
  const [onboardErr, setOnboardErr] = useState("");           // sichtbare Fehler-Box (Runner-Fehler ODER hartes Backend-Tot) — FATAL
  const [stalled, setStalled] = useState(false);             // nicht-fatal: Endpoint kurz stumm (Blip) → Reconnect-Banner, Polling läuft weiter
  const lastOkAtRef = useRef(0);                              // Zeitstempel des letzten GÜLTIGEN /onboard/status (Watchdog)
  const runConfirmedRef = useRef(false);                     // ist DIESER Lauf per start() bestätigt? Stale-Guard gegen done:true aus dem Vorlauf (gesetzt nach start(), NICHT erst bei beobachtetem running:true — sonst verpasst schnelle Läufe)

  // Polling-Spiegel des Optimizers (index.jsx): useApi + ok()-Guard.
  const { data: onbStRaw } = useApi("/onboard/status", onboarding ? 3000 : 0);
  const onb = ok(onbStRaw) ? onbStRaw : {};
  // Optimizer-Tunable-Registry (einmalig, interval 0) — Quelle für Block F (vom Optimizer
  // gesuchte Params + Code-Defaults). Driftfrei (Backend-Registry, kein Frontend-Hardcode).
  const { data: tunablesRaw } = useApi("/optimizer/tunables", 0);
  const tunables = (ok(tunablesRaw) && tunablesRaw.tunables) || [];

  // Anchor-Symbole = Strategien mit existierender _calib.yaml (onboard-Anchor-Pflicht).
  const anchorSymbols = useMemo(() => {
    const names = (files || []).map(f => typeof f === "string" ? f : (f.filename || f.name || ""));
    return [...new Set(names
      .filter(n => /_v7_1_aggressive_consensus_calib\.yaml$/.test(n))
      .map(n => n.split("_")[0].toUpperCase()))];
  }, [files]);

  const canNext1 = inst && (sourceMode === "scratch" || anchorSym);

  // Objective = Navigations-Hint: welche der zwei erzeugten YAMLs am Ende geöffnet wird.
  const chosenFile = () => {
    const s = (inst || "").toLowerCase();
    return objective === "robust"
      ? `${s}_v7_1_aggressive_consensus_calib.yaml`
      : `${s}_v7_1_pnlmax_experiment.yaml`;
  };

  // T3-B1/B2/B3: Lauf-Parameter clientseitig validieren (Backend re-validiert hart).
  const validateRunParams = () => {
    if (!(TF_MIN[tfEntry] < TF_MIN[tfContext] && TF_MIN[tfContext] < TF_MIN[tfRegime]))
      return `TF-Hierarchie ungültig: entry(${tfEntry}) < context(${tfContext}) < regime(${tfRegime}) erforderlich`;
    if (TF_MIN[tfEntry] < TF_MIN.M5)
      return `Sub-M5-Entry (${tfEntry}) braucht einen feinen M1-Basis-Feed — bitte sicherstellen, dass M1-Daten vorliegen`;
    if (!startDate || !endDate || startDate >= endDate)
      return `Zeitraum ungültig: Start (${startDate}) muss vor Ende (${endDate}) liegen`;
    return null;
  };

  const runOnboard = async () => {
    const verr = validateRunParams();
    if (verr) { toast(verr, "error"); setOnboardErr(verr); return; }   // gar nicht erst launchen
    setLaunched(true);
    setOnboarding(true);
    setOnboardErr("");
    setStalled(false);
    runConfirmedRef.current = false;   // erst zurücksetzen: ein stale done:true aus dem Vorlauf (vor diesem start()) wird geblockt
    lastOkAtRef.current = Date.now();   // Watchdog-Start: Lauf gilt zunächst als frisch erreichbar
    const res = await postApi("/onboard/start", {
      symbol: inst,
      anchor: sourceMode === "existing" ? anchorSym : null,
      anchor_file: sourceMode === "scratch" ? SEED_ANCHOR_FILE : null,
      dry_run: false,
      // T3-B1: nur senden, wenn von Default abweichend → sonst onboard byte-erhaltend (TF-Block unangetastet)
      ...((tfEntry !== "M5" || tfContext !== "H1" || tfRegime !== "D")
        ? { timeframes: { entry: tfEntry, context: tfContext, regime: tfRegime } } : {}),
      start: startDate,
      end: endDate,
      // T0-ARCH: kein n_trials/seed mehr — Onboarding optimiert nicht.
    });
    if (res?.error) { toast(res.error, "error"); setOnboarding(false); setOnboardErr(res.error); return; }
    // DIESER Lauf ist bestätigt gestartet (Backend setzt _running=True + manifest=None
    // synchron in start()) → ab jetzt darf done/error honoriert werden, AUCH ohne je
    // einen running:true-Poll gesehen zu haben (schneller calib-loser Lauf, T0-ARCH).
    runConfirmedRef.current = true;
  };
  const stopOnboard = () => { postApi("/onboard/stop"); setOnboarding(false); };

  // done/error: Polling stoppen, dann die Ranges-Übersicht (step 3) zeigen.
  // Guard (classifyCompletion): done/error nur honorieren, wenn DIESER Lauf per start()
  // bestätigt ist (runConfirmedRef) — blockt ein stale done:true aus dem Vorlauf, greift
  // aber auch bei schnellen calib-losen Läufen ohne je beobachtetes running:true.
  // Dep auf onbStRaw (jeder Poll = neue Referenz): so wird die Completion bei JEDEM
  // frischen Poll neu geprüft — auch wenn onb.done nicht von false→true wechselt (z.B.
  // stale-true direkt gefolgt von this-run-true).
  useEffect(() => {
    if (!onboarding) return;   // nur einen AKTIVEN Lauf abschließen → kein Doppel-Toast bei spätem In-Flight-Poll
    const verdict = classifyCompletion({
      launched, statusOk: ok(onbStRaw), runConfirmed: runConfirmedRef.current,
      done: !!onb.done, error: !!onb.error,
    });
    if (verdict === "done") {
      setOnboarding(false);
      toast(`Onboarding fertig: ${inst}`, "success");
      // T2-C2/T0-ARCH: KEIN Auto-Sprung — erst die Ranges-Übersicht zeigen; der User
      // wählt bewusst "Jetzt Optimieren" oder "Im Editor öffnen" (Render im step===3).
    } else if (verdict === "error") {
      setOnboarding(false);
      setOnboardErr(onb.error);
      toast(onb.error, "error");
    }
  }, [onbStRaw, onb.done, onb.error, launched, onboarding]);  // eslint-disable-line

  // Watchdog 1: lastOkAt frisch halten, solange GÜLTIGE Status ankommen. Ein langsamer
  // Lauf liefert weiter "running"-Status (ok==true) → der Zeitstempel bleibt frisch.
  // Reconnect-Adoption: setData() liefert immer eine NEUE Referenz (auch bei gleichem
  // Inhalt) → nach einem Blip feuert dieser Effekt beim ersten erfolgreichen Poll und
  // löscht den stalled-Zustand; der normale Fluss (onb.done/onb.error) übernimmt wieder.
  useEffect(() => { if (ok(onbStRaw)) { lastOkAtRef.current = Date.now(); setStalled(false); } }, [onbStRaw]);

  // Watchdog 2: zweistufig (classifyStall). Eigener Intervall, weil useApi bei wiederholt
  // identischem Fehler kein Re-Render auslöst (genau das verursachte den Hang).
  //  - "soft" (>15s stumm): nur Reconnect-Banner; onboarding BLEIBT true → useApi pollt
  //    weiter (Intervall an onboarding gekoppelt, Z.~752). Heilt der Tunnel, adoptiert
  //    Watchdog 1 den Fluss wieder. KEIN setOnboarding(false) → keine Abkopplung (B3-Fix).
  //  - "hard" (>90s stumm): Backend gilt als tot → bisheriger harter Pfad (Abbruch +
  //    Fehler-Box + Toast). Bewahrt den 0e080a0-Zweck (kein Endlos-Hang bei echtem Tod).
  useEffect(() => {
    if (!onboarding || !launched) return;
    const id = setInterval(() => {
      const level = classifyStall(Date.now() - lastOkAtRef.current);
      if (level === "soft") {
        setStalled(true);
      } else if (level === "hard") {
        setOnboarding(false);
        const msg = "Backend nicht erreichbar — Onboarding-Status verloren";
        setOnboardErr(msg);
        toast(msg, "error");
      }
    }, 3000);
    return () => clearInterval(id);
  }, [onboarding, launched]);  // eslint-disable-line

  const RadioCard = ({ active, onClick, title, desc }) => (
    <div onClick={onClick} style={{ flex: 1, padding: "14px 16px", borderRadius: 12, cursor: "pointer",
      border: `2px solid ${active ? theme.blue : theme.border}`,
      background: active ? "rgba(37,99,235,0.05)" : theme.white }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: active ? theme.blueText : theme.text, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12, color: theme.text3, lineHeight: 1.5 }}>{desc}</div>
    </div>
  );

  return <Card style={{ maxWidth: 720, margin: "0 auto" }}>
    <div style={{ padding: "20px 28px 16px", borderBottom: `1px solid ${theme.divider}` }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: theme.text, marginBottom: 8 }}>Neue Strategie onboarden</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {[{ n: 1, l: "Instrument & Quelle" }, { n: 2, l: "Ziel" }, { n: 3, l: "Onboarden" }].map((s, i) => (
          <div key={s.n} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ padding: "5px 12px", borderRadius: 8, fontSize: 13, fontWeight: step === s.n ? 700 : 500,
              background: step === s.n ? theme.blue : step > s.n ? theme.greenLight : theme.tint,
              color: step === s.n ? "#fff" : step > s.n ? theme.greenText : theme.text4 }}>
              {step > s.n ? "✓ " : `${s.n}. `}{s.l}
            </div>
            {i < 2 && <span style={{ color: theme.text5 }}>→</span>}
          </div>
        ))}
      </div>
    </div>

    <div style={{ padding: "24px 28px" }}>
      {step === 1 && <>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: theme.text }}>Instrument</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20 }}>
          {ALL_INSTRUMENTS.map(i => { const m = INST_META[i]; const active = inst === i;
            return <div key={i} onClick={() => setInst(i)} style={{ padding: "16px", borderRadius: 12, cursor: "pointer",
              border: `2px solid ${active ? theme.blue : theme.border}`, background: active ? "rgba(37,99,235,0.05)" : theme.white }}>
              <div style={{ fontSize: 18, fontWeight: 900, fontFamily: fonts.mono, color: active ? theme.blueText : theme.text }}>{i}</div>
              <div style={{ fontSize: 11, color: theme.text3 }}>{m.exchange}</div>
            </div>;
          })}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: theme.text }}>Quelle</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <RadioCard active={sourceMode === "scratch"} onClick={() => setSourceMode("scratch")}
            title="🌱 Von Grund auf" desc="Neutraler v4-Seed als Vorlage — die Pipeline misst und optimiert alles." />
          <RadioCard active={sourceMode === "existing"} onClick={() => setSourceMode("existing")}
            title="📋 Von bestehender Strategie" desc="Eine kalibrierte Strategie als Anchor klonen und neu kalibrieren." />
        </div>
        {sourceMode === "existing" && <F label="Anchor-Strategie (Symbol mit Calib)">
          <select value={anchorSym} onChange={e => setAnchorSym(e.target.value)} style={{ ...sel, fontSize: 13 }}>
            <option value="">— wählen —</option>
            {anchorSymbols.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </F>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          {onCancel && <Button onClick={onCancel}>Abbrechen</Button>}
          <Button primary disabled={!canNext1} onClick={() => setStep(2)}>Weiter →</Button>
        </div>
      </>}

      {step === 2 && <>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: theme.text }}>Optimierungs-Ziel</div>
        <div style={{ fontSize: 13, color: theme.text3, marginBottom: 14 }}>
          Onboarding steckt für BEIDE Ziele die Such-Räume ab — die Wahl bestimmt, welche der zwei Range-Dateien am Ende fürs Optimieren vorgeschlagen wird.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <RadioCard active={objective === "robust"} onClick={() => setObjective("robust")}
            title="🛡 Robust" desc="bucket_robust_score — Drawdown-Kontrolle, Bucket-Robustheit (Default)." />
          <RadioCard active={objective === "pnl"} onClick={() => setObjective("pnl")}
            title="💵 PnL-Max" desc="oos_consistent_pnl — konsistenter OOS-PnL." />
        </div>

        {/* T3-B1/B2: Onboarding-Lauf-Parameter. n_trials/seed sind hier WEG (T0-ARCH:
            Onboarding optimiert nicht — die gehören in den Optimieren-Schritt). */}
        <div style={{ fontSize: 15, fontWeight: 700, margin: "20px 0 4px", color: theme.text }}>Lauf-Parameter (Ranges-Ableitung)</div>
        <div style={{ fontSize: 12, color: theme.text3, marginBottom: 10 }}>
          Default M5/H1/D · 2022-01-01…2026-04-01. Freie TF-Triaden werden von Engine/Live unterstützt (§4.3, empirisch bestätigt); entry &lt; context &lt; regime erforderlich. (n_trials/Seed setzt du erst im Optimieren-Schritt.)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 8 }}>
          <F label="Entry TF"><select value={tfEntry} onChange={e => setTfEntry(e.target.value)} style={sel}>{TF_ENTRY_OPTS.map(t => <option key={t}>{t}</option>)}</select></F>
          <F label="Context TF"><select value={tfContext} onChange={e => setTfContext(e.target.value)} style={sel}>{TF_CONTEXT_OPTS.map(t => <option key={t}>{t}</option>)}</select></F>
          <F label="Regime TF"><select value={tfRegime} onChange={e => setTfRegime(e.target.value)} style={sel}>{TF_REGIME_OPTS.map(t => <option key={t}>{t}</option>)}</select></F>
          <F label="Start"><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inp} /></F>
          <F label="Ende"><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inp} /></F>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
          <Button onClick={() => setStep(1)}>← Zurück</Button>
          <Button primary onClick={() => setStep(3)}>Weiter →</Button>
        </div>
      </>}

      {step === 3 && <>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: theme.text }}>Zusammenfassung</div>
        <div style={{ padding: "16px 20px", borderRadius: 12, background: theme.tint, border: `1px solid ${theme.divider}`, marginBottom: 16, fontSize: 13, lineHeight: 1.8 }}>
          <div>Instrument: <b style={{ fontFamily: fonts.mono }}>{inst}</b></div>
          <div>Quelle: <b>{sourceMode === "scratch" ? "Von Grund auf (Seed)" : `Anchor ${anchorSym}`}</b></div>
          <div>Ziel-Datei: <b>{objective === "robust" ? "Robust (_calib)" : "PnL-Max (_pnlmax_experiment)"}</b></div>
          <div>TF: <b style={{ fontFamily: fonts.mono }}>{tfEntry}/{tfContext}/{tfRegime}</b> · Zeitraum: <b>{startDate}…{endDate}</b></div>
        </div>
        {/* Lauf-Status (Polling-Spiegel des Optimizers) */}
        {launched && onboarding && <div style={{ padding: "12px 16px", borderRadius: 8, background: theme.blueLight, border: `1px solid ${theme.blue}`, fontSize: 13, color: theme.text2, marginBottom: 16 }}>
          ⏳ <b>Läuft…</b> {onb.status_message || "Onboarding-Pipeline"} {onb.symbol ? `(${onb.symbol})` : ""}
          {onb.stages_progress && <div style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 700 }}>
              Stufe {onb.stages_progress.current}/{onb.stages_progress.total} · {ONBOARD_STAGE_DE[onb.stages_progress.current_stage] || onb.stages_progress.current_stage}
            </div>
            {(onb.stages_progress.done || []).map(d => {
              const sum = onboardStageSummary(d.name, d.summary);
              return <div key={d.name} style={{ fontSize: 12, color: theme.text2, opacity: 0.85 }}>
                ✓ {ONBOARD_STAGE_DE[d.name] || d.name}{sum ? ` — ${sum}` : ""}
              </div>;
            })}
          </div>}
        </div>}
        {launched && onboarding && stalled && <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: `1px solid ${theme.amber || "#d97706"}`, fontSize: 13, color: theme.amber || "#b45309", marginBottom: 16 }}>
          ⟳ <b>Verbindung unterbrochen</b> — versuche, den Lauf wieder zu erreichen… (der Lauf läuft im Hintergrund weiter)
        </div>}
        {launched && !onboarding && (onboardErr || onb.error) && <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: `1px solid ${theme.redText}`, fontSize: 13, color: theme.redText, marginBottom: 16 }}>
          ✗ Fehler: {onboardErr || onb.error}
        </div>}
        {launched && !onboarding && onb.done && (() => {
          // T0-ARCH: vollständiger „Was hat das Onboarding gesetzt"-Screen. Volle
          // Suchraum-Transparenz über ALLE 12 Optimizer-Params (registry-getrieben), plus
          // caps/TF/Mess-Kontext. Quellen NUR aus manifest + /optimizer/tunables — KEIN
          // Frontend-Hardcode von Seed-/Optimizer-Werten.
          const m = onb.manifest || {};
          const stg = m.stages || {};
          const ar = m.artifact_ranges || {};                      // B (+A): volle goals.ranges aus dem Artefakt
          const derivedKeys = new Set(Object.keys((stg.ranges && stg.ranges.ranges) || {})); // A: derive_ranges-Keys
          const cp = (stg.caps && stg.caps.caps) || {};            // C
          const ms = stg.measure || {};                            // E
          const tf = m.timeframes || {};                           // D
          const per = m.period || {};                              // E
          const has = (v) => v !== undefined && v !== null;
          const Row = ({ label, children }) => (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: theme.text2, padding: "2px 0", gap: 8 }}>
              <span>{label}</span><b style={{ fontFamily: fonts.mono, textAlign: "right" }}>{children}</b>
            </div>
          );
          return <div style={{ marginBottom: 16 }}>
            {/* G — Hinweis */}
            <div style={{ padding: "14px 18px", borderRadius: 10, background: theme.greenLight, border: `1px solid ${theme.green}`, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: theme.greenText }}>
                ✓ Such-Räume abgesteckt für {inst} — bereit für Optimieren
              </div>
              <div style={{ fontSize: 12, color: theme.text3, marginTop: 4 }}>
                Onboarding setzt nur Suchräume, optimiert nicht (§0): die konkreten Werte findet der
                Optimieren→Walk-Forward→Apply-Zyklus. Artefakt: <code>status:test</code> · <code>_meta.status:needs_optimize</code>.
              </div>
            </div>

            {/* A+B+F — vollständiger Optimizer-Suchraum (alle 12 Params, registry-getrieben) */}
            <div style={{ fontWeight: 700, fontSize: 13, color: theme.text, margin: "0 0 6px" }}>
              Optimizer-Suchraum — alle {tunables.length || 12} Parameter
            </div>
            <div style={{ borderRadius: 10, border: `1px solid ${theme.border}`, overflow: "hidden", marginBottom: 12 }}>
              {tunables.map((t, i) => {
                const src = tunableSource(t, ar, derivedKeys);
                const badge = TUNABLE_SOURCE_BADGE[src];
                return <div key={t.param} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                  padding: "7px 12px", fontSize: 12, background: i % 2 ? theme.tint : theme.white, color: theme.text2 }}>
                  <span style={{ minWidth: 150, color: theme.text }}>{t.label}{t.condition ? <span style={{ color: theme.text4, fontSize: 11 }}> · {t.condition}</span> : null}</span>
                  <b style={{ fontFamily: fonts.mono, flex: 1, textAlign: "right" }}>{tunableValue(t, ar)}</b>
                  <span style={{ minWidth: 200, textAlign: "right" }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 6, background: badge.bg, color: badge.fg }}>{badge.txt}</span>
                  </span>
                </div>;
              })}
            </div>

            {/* C + D + E */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div style={{ padding: "12px 14px", borderRadius: 10, background: theme.white, border: `1px solid ${theme.border}` }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: theme.text, marginBottom: 6 }}>Risk-Caps + Timeframes</div>
                {has(cp.max_contracts) && <Row label="max_contracts">{cp.max_contracts}</Row>}
                {has(cp.min_sl_points) && <Row label="min_sl_points">{cp.min_sl_points}</Row>}
                {has(cp.min_trade_qty) && <Row label="min_trade_qty">{cp.min_trade_qty}</Row>}
                <Row label="TF (entry/ctx/regime)">{tf.entry}/{tf.context}/{tf.regime}</Row>
              </div>
              <div style={{ padding: "12px 14px", borderRadius: 10, background: theme.white, border: `1px solid ${theme.border}` }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: theme.text, marginBottom: 6 }}>Mess-Kontext</div>
                {has(ms.n_entries) && <Row label="Entries / Winners">{ms.n_entries} / {ms.n_winners != null ? ms.n_winners : "—"}</Row>}
                {has(pick(ms.mfe_R, 50)) && <Row label="MFE R p50/75/95">{fmtNum(pick(ms.mfe_R, 50))}/{fmtNum(pick(ms.mfe_R, 75))}/{fmtNum(pick(ms.mfe_R, 95))}</Row>}
                {has(pick(ms.mae_win_R, 75)) && <Row label="MAE-win R p75">{fmtNum(pick(ms.mae_win_R, 75))}</Row>}
                {has(pick(ms.sl_dist_atr, 50)) && <Row label="sl_dist ATR p5/50/95">{fmtNum(pick(ms.sl_dist_atr, 5))}/{fmtNum(pick(ms.sl_dist_atr, 50))}/{fmtNum(pick(ms.sl_dist_atr, 95))}</Row>}
                {(per.start || per.end) && <Row label="Zeitraum">{per.start}…{per.end}</Row>}
              </div>
            </div>

            {/* T0-ARCH/T3-D2: nächster Soll-Schritt = OPTIMIEREN (nicht WF — der kommt danach). */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {onOptimize && <Button primary onClick={() => onOptimize(chosenFile(), inst)}>▶ Jetzt Optimieren</Button>}
              <Button primary={!onOptimize} onClick={() => onCreated?.(chosenFile(), inst)}>{chosenFile()} im Editor öffnen →</Button>
            </div>
          </div>;
        })()}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={() => setStep(2)} disabled={onboarding}>← Zurück</Button>
          {onboarding
            ? <Button onClick={stopOnboard}>Stoppen</Button>
            : <Button primary onClick={runOnboard}>{(launched && (onb.done || onboardErr || onb.error)) ? "Erneut onboarden" : "Onboarden"}</Button>}
        </div>
      </>}
    </div>
  </Card>;
}

/* ═══════════════════════════════════════════════════
   MAIN BUILDER TAB
   ═══════════════════════════════════════════════════ */
export default function BuilderTab({ files, filesLoading, onRefetch, onSelectStrategy, onOptimize, selectedFile }) {
  const [editFile, setEditFile] = useState(selectedFile || null);
  const [rawYaml, setRawYaml] = useState("");
  const [editLd, setEditLd] = useState(false);
  const [mode, setMode] = useState("form");
  const [form, setForm] = useState({...DEF_FORM});
  // Stufe 2A: Artefakt-Status aus der ROHEN cfg (NICHT ins Form → kein formToConfig-Einfluss).
  // {meta:_meta.status, pipeline:pipeline.status} — Quelle für die Seed-Rest-Kennzeichnung.
  const [artifactStatus, setArtifactStatus] = useState(null);
  // Etappe 3: Lifecycle/Identitäts-Felder aus der ROHEN cfg (das Form trägt _meta/
  // pipeline/strategy_family/created_at nicht). Read-only Anzeige, kein Schreibpfad.
  const [lifecycle, setLifecycle] = useState(null);
  const [showYaml, setShowYaml] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const sf = (k,v) => setForm(p => ({...p, [k]:v}));

  // ── Strategy-Lab overview state ──
  const [runners, setRunners] = useState({});     // strategy_key → runner state (real run-status)
  const [fAsset, setFAsset] = useState("Alle");
  const [fPurpose, setFPurpose] = useState("Alle");
  const [fStatus, setFStatus] = useState("Alle");
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => { if (selectedFile && selectedFile !== editFile) loadFile(selectedFile); }, [selectedFile]);

  // Real run-status: a runner only exists when the bot actually runs a strategy
  // (YAML status paper/live). No runner → "Aus". Authoritative over the YAML field.
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/runners/status?_t=${Date.now()}`)
      .then(r => r.json())
      .then(d => { if (alive) setRunners(d?.runners || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [files]);

  const loadFile = async n => {
    setEditFile(n); setEditLd(true); setShowWizard(false);
    try {
      const r = await fetch(`${API_BASE}/strategy-files/${encodeURIComponent(n)}?_t=${Date.now()}`);
      const d = await r.json();
      const raw = d.raw || (typeof d === "string" ? d : d.content || JSON.stringify(d,null,2));
      setRawYaml(raw);
      const cfg = d.config || (typeof d === "object" && !d.raw ? d : null);
      if (cfg) { const parsed = parseYamlToForm(cfg); if (parsed) setForm(parsed); }
      // Stufe 2A: Status aus der rohen cfg festhalten (read-only, separat vom Form).
      setArtifactStatus(cfg ? { meta: cfg._meta?.status, pipeline: cfg.pipeline?.status } : null);
      setLifecycle(cfg ? {
        strategy_family: cfg.strategy_family,
        _meta: cfg._meta || null,
        pipeline_status: cfg.pipeline?.status,
        pipeline_history_n: Array.isArray(cfg.pipeline?.history) ? cfg.pipeline.history.length : null,
        created_at: cfg.created_at,
        updated_at: cfg.updated_at,
        // B8: Ranges-Stand beim Laden — Dirty-Flag erkennt Range-Edits an einem schon
        // optimierten Artefakt (kein persistenter Snapshot im pipeline-Block vorhanden).
        ranges_at_load: cfg.goals?.ranges ? JSON.parse(JSON.stringify(cfg.goals.ranges)) : {},
        broker_type: cfg.broker?.type,
        broker_account_type: cfg.broker?.account_type,
        broker_paper_account: cfg.broker?.paper_account,
        instrument_sec_type: cfg.instrument?.sec_type,
        instrument_currency: cfg.instrument?.currency,
      } : null);
      setMode("form");
    } catch { setRawYaml("Error loading"); }
    setEditLd(false);
  };

  const saveExistingAsConfig = async () => {
    const errs = validateForm(form);
    if (errs.length) { toast(errs.join(". "), "error"); return; }
    if (!editFile) return;
    const config = formToConfig(form);
    const res = await postApi("/strategy-files", { filename:editFile, config, force:false });
    if (res && !res.detail) { toast(`Gespeichert: ${res.filename||editFile}`); onRefetch?.(); }
    else toast(`Error: ${res?.detail||"Live-Strategien sind schreibgeschützt."}`, "error");
  };

  const cloneFile = async () => {
    if (!editFile) return;
    const n = prompt("Clone als:", editFile.replace(".yaml","_copy.yaml"));
    if (n) {
      const res = await postApi(`/strategy-files/${encodeURIComponent(editFile)}/clone`, {new_filename:n});
      if (res && !res.detail) {
        // R7b-4: Warn if source had advanced pipeline status — clone starts fresh
        if (res.was_reset) {
          toast(`Cloned! Pipeline wurde zurückgesetzt — ${n} startet frisch (created), nicht optimiert`, "warn");
        } else {
          toast("Cloned!");
        }
      } else {
        toast(`Error: ${res?.detail || "Fehlgeschlagen"}`, "error");
      }
      onRefetch?.();
    }
  };

  const deleteFile = async () => {
    if (!editFile) return;
    showModal("Löschen", `Strategie ${editFile} archivieren?`, async () => {
      try { await fetch(`${API_BASE}/strategy-files/${encodeURIComponent(editFile)}`, {method:"DELETE"}); setEditFile(null); toast("Archiviert"); onRefetch?.(); }
      catch(e) { toast("Error: "+e.message, "error"); }
    });
  };

  const renameFile = async () => {
    if (!editFile) return;
    const newName = prompt("Neuer Name:", editFile);
    if (!newName || newName === editFile) return;
    const sanitized = newName.endsWith(".yaml") ? newName : newName + ".yaml";
    const existing = files.find(f => (f.filename||f.name) === sanitized);
    if (existing) { toast("Name existiert bereits.", "error"); return; }
    const cloneRes = await postApi(`/strategy-files/${encodeURIComponent(editFile)}/clone`, { new_filename: sanitized });
    if (cloneRes && !cloneRes.detail) {
      await fetch(`${API_BASE}/strategy-files/${encodeURIComponent(editFile)}`, { method:"DELETE" });
      toast(`Umbenannt: ${editFile} → ${sanitized}`);
      onRefetch?.(); onSelectStrategy?.(sanitized, form.inst); loadFile(sanitized);
    } else { toast(`Error: ${cloneRes?.detail || "Fehlgeschlagen"}`, "error"); }
  };

  // ── Archive / Restore (no hard-delete — _meta.status is authoritative) ──
  const archiveStrategy = (n) => {
    showModal("Archivieren?", `${n} ins Archiv verschieben? (kein Löschen — jederzeit wiederherstellbar)`, async () => {
      try {
        await fetch(`${API_BASE}/strategy-files/${encodeURIComponent(n)}/meta`, {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "archived" }),
        });
        toast("Archiviert: " + n); onRefetch?.();
      } catch { toast("Archivieren fehlgeschlagen", "error"); }
    });
  };
  const restoreStrategy = async (f) => {
    const n = f.filename || f.name;
    try {
      // Restore never auto-promotes to deployable — safe default wf_required.
      await fetch(`${API_BASE}/strategy-files/${encodeURIComponent(n)}/meta`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "wf_required" }),
      });
      if (f.status === "archived") {
        await fetch(`${API_BASE}/strategy-files/${encodeURIComponent(n)}/status`, {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "test" }),
        });
      }
      toast("Wiederhergestellt: " + n); onRefetch?.();
    } catch { toast("Wiederherstellen fehlgeschlagen", "error"); }
  };

  const yamlStr = useMemo(() => formToYamlString(form), [form]);

  /* ── Wizard Mode ────────────────────────── */
  // v4-FIX: Don't flash the wizard during initial load. Only show it if the fetch
  // has completed AND there are no files (or user explicitly opened it).
  if (filesLoading && !editFile && files.length === 0) {
    return <Card><div style={{ padding:40, textAlign:"center" }}><LoadingState/></div></Card>;
  }
  if (showWizard || (!editFile && files.length === 0)) {
    return <OnboardWizard files={files}
      onCreated={(fn, inst) => { setShowWizard(false); onRefetch?.(); onSelectStrategy?.(fn, inst); loadFile(fn); }}
      onOptimize={onOptimize ? (fn, inst) => { setShowWizard(false); onRefetch?.(); onOptimize(fn, inst); } : null}
      onCancel={files.length > 0 ? () => setShowWizard(false) : null}
    />;
  }

  /* ── Strategy-Lab Overview (no file selected) ─────────────── */
  if (!editFile) {
    const PURPOSE = {
      live:       { label: "Prod-Ziel",  variant: "green",   ord: 0 },
      calib:      { label: "Calib",      variant: "amber",   ord: 1 },
      experiment: { label: "Experiment", variant: "purple",  ord: 2 },
      reference:  { label: "Referenz",   variant: "default", ord: 3 },
    };
    const META_STATUS = {
      deployable:   { label: "deploy-fähig",  variant: "green" },
      wf_required:  { label: "WF nötig",      variant: "amber" },
      experimental: { label: "experimentell", variant: "purple" },
      archived:     { label: "archiviert",    variant: "default" },
    };
    const ASSET_ORDER = ["MGC", "MNQ", "MES", "MYM"];
    const miniBtnStyle = {
      fontSize:10.5, fontWeight:700, padding:"4px 9px", borderRadius:7, cursor:"pointer",
      border:`1px solid ${theme.border}`, background:theme.white, color:theme.text4,
    };

    // Real run-state from the runner manager (authoritative; the YAML status field
    // alone can mislead — a runner only exists when the bot actually runs it).
    const runStateOf = (f) => {
      const key = (f.filename || f.name || "").replace(/\.yaml$/, "");
      const r = runners[key];
      if (!r || !r.is_running) return { label:"Aus",    dot:theme.text4, bg:theme.tint,       color:theme.text3,     pulse:false };
      if (r.status === "live")  return { label:"Scharf", dot:theme.green, bg:theme.greenLight, color:theme.greenText, pulse:true };
      if (r.status === "paper") return { label:"Paper",  dot:theme.blue,  bg:theme.blueLight,  color:theme.blueText,  pulse:false };
      return                            { label:"Läuft",  dot:theme.blue,  bg:theme.blueLight,  color:theme.blueText,  pulse:false };
    };

    const isArchived = (f) => (f.meta?.status === "archived") || (f.status === "archived");
    const assetOf = (f) => f.meta?.asset || f.instrument || "?";

    const strategies = files.filter(f => typeof f === "object" && !f.is_experiment);
    const visible = strategies.filter(f => {
      if (!showArchived && isArchived(f)) return false;
      if (fAsset   !== "Alle" && assetOf(f)              !== fAsset)   return false;
      if (fPurpose !== "Alle" && (f.meta?.purpose || "—") !== fPurpose) return false;
      if (fStatus  !== "Alle" && (f.meta?.status  || "—") !== fStatus)  return false;
      return true;
    });

    const unclassified = visible.filter(f => !f.meta || !f.meta.purpose);
    const classified   = visible.filter(f => f.meta && f.meta.purpose);

    const groups = {};
    classified.forEach(f => { (groups[assetOf(f)] ||= []).push(f); });
    Object.values(groups).forEach(arr => arr.sort((a,b) =>
      (PURPOSE[a.meta?.purpose]?.ord ?? 9) - (PURPOSE[b.meta?.purpose]?.ord ?? 9)));
    const groupKeys = Object.keys(groups).sort((a,b) => {
      const ia = ASSET_ORDER.indexOf(a), ib = ASSET_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    const archivedCount = strategies.filter(isArchived).length;

    const FilterRow = ({ label, value, set, opts }) => (
      <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
        <span style={{ fontSize:11, fontWeight:700, color:theme.text4, minWidth:54 }}>{label}</span>
        {opts.map(o => (
          <button key={o} onClick={() => set(o)} style={{
            fontSize:11, fontWeight:700, padding:"4px 10px", borderRadius:7, cursor:"pointer",
            border:`1px solid ${value===o ? theme.blue : theme.border}`,
            background: value===o ? theme.blue : theme.white, color: value===o ? "#fff" : theme.text3,
          }}>{o}</button>
        ))}
      </div>
    );

    const renderCard = (f, i) => {
      const n = f.filename || f.name || "";
      const p = PURPOSE[f.meta?.purpose];
      const s = META_STATUS[f.meta?.status];
      const rs = runStateOf(f);
      const archived = isArchived(f);
      return (
        <Card key={n||i} className="sl-card" style={{ cursor:"pointer", opacity: archived ? 0.72 : 1 }}
              onClick={() => { loadFile(n); onSelectStrategy?.(n, assetOf(f)); }}>
          <div style={{ padding:"14px 16px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, marginBottom:8 }}>
              <span style={{ fontSize:15, fontWeight:800, fontFamily:fonts.mono }}>{assetOf(f)}</span>
              <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", justifyContent:"flex-end" }}>
                {p && <Badge variant={p.variant}>{p.label}</Badge>}
                {s && <Badge variant={s.variant}>{s.label}</Badge>}
              </div>
            </div>
            <div style={{ fontSize:11.5, fontFamily:fonts.mono, color:theme.text3, marginBottom:6, wordBreak:"break-all" }}>{n}</div>
            {f.meta?.description && (
              <div style={{ fontSize:11.5, color:theme.text2, lineHeight:1.5, marginBottom:8, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{f.meta.description}</div>
            )}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:10.5, color:theme.text4 }}>
                <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 9px", borderRadius:20, background:rs.bg, color:rs.color, fontWeight:700 }}>
                  <span style={{ width:7, height:7, borderRadius:"50%", background:rs.dot, animation: rs.pulse ? "liveDot 2.5s infinite" : "none" }}/>
                  {rs.label}
                </span>
                <span style={{ fontFamily:fonts.mono }}>v{f.version||"?"}</span>
                {f.meta?.parent && <span title={`abgeleitet von ${f.meta.parent}`}>↳ {f.meta.parent.replace(/\.yaml$/,"")}</span>}
              </div>
              {archived
                ? <button onClick={e => { e.stopPropagation(); restoreStrategy(f); }} style={miniBtnStyle}>Wiederherstellen</button>
                : f.status !== "live" && <button onClick={e => { e.stopPropagation(); archiveStrategy(n); }} title="Archivieren" style={miniBtnStyle}>Archivieren</button>}
            </div>
          </div>
        </Card>
      );
    };

    return <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:800, color:theme.text }}>Strategy Lab</div>
          <div style={{ fontSize:13, color:theme.text3 }}>{visible.length} sichtbar · nach Asset gruppiert, Lauf-Status live.</div>
        </div>
        <Button primary onClick={() => setShowWizard(true)} style={{ padding:"10px 20px", fontSize:14 }}>+ Neue Strategie</Button>
      </div>

      <Card style={{ marginBottom:16 }}>
        <div style={{ padding:"12px 16px", display:"flex", flexDirection:"column", gap:8 }}>
          <FilterRow label="Asset"  value={fAsset}   set={setFAsset}   opts={["Alle", ...ASSET_ORDER]} />
          <FilterRow label="Zweck"  value={fPurpose} set={setFPurpose} opts={["Alle","live","calib","experiment","reference"]} />
          <FilterRow label="Status" value={fStatus}  set={setFStatus}  opts={["Alle","deployable","wf_required","experimental","archived"]} />
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:2 }}>
            <span style={{ fontSize:11, fontWeight:700, color:theme.text4, minWidth:54 }}>Archiv</span>
            <button onClick={() => setShowArchived(v => !v)} style={{
              fontSize:11, fontWeight:700, padding:"4px 10px", borderRadius:7, cursor:"pointer",
              border:`1px solid ${showArchived ? theme.blue : theme.border}`,
              background: showArchived ? theme.blue : theme.white, color: showArchived ? "#fff" : theme.text3,
            }}>{showArchived ? "eingeblendet" : "ausgeblendet"} ({archivedCount})</button>
          </div>
        </div>
      </Card>

      {unclassified.length > 0 && (
        <div style={{ marginBottom:18 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
            <span style={{ fontSize:12, fontWeight:800, color:theme.amberText, textTransform:"uppercase", letterSpacing:"0.5px" }}>⚠ Unklassifiziert</span>
            <span style={{ fontSize:11, color:theme.text4 }}>{unclassified.length} ohne _meta — bitte klassifizieren</span>
          </div>
          <div style={{ border:`1px solid ${theme.amber}`, borderRadius:14, padding:10, background:theme.amberLight }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:10 }}>
              {unclassified.map(renderCard)}
            </div>
          </div>
        </div>
      )}

      {groupKeys.map(gk => (
        <div key={gk} style={{ marginBottom:18 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
            <span style={{ fontSize:13, fontWeight:800, fontFamily:fonts.mono, color:theme.text }}>{gk}</span>
            <span style={{ flex:1, height:1, background:theme.divider }}/>
            <span style={{ fontSize:11, color:theme.text4 }}>{groups[gk].length} {groups[gk].length===1?"Strategie":"Strategien"}</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:10 }}>
            {groups[gk].map(renderCard)}
          </div>
        </div>
      ))}

      {visible.length === 0 && (
        <Card><div style={{ padding:30, textAlign:"center", color:theme.text4, fontSize:13 }}>Keine Strategien für diesen Filter.</div></Card>
      )}

      <Card className="sl-card" style={{ cursor:"pointer", border:`2px dashed ${theme.border}`, maxWidth:260, marginTop:6 }} onClick={() => setShowWizard(true)}>
        <div style={{ padding:"20px 18px", textAlign:"center" }}>
          <div style={{ fontSize:24, marginBottom:4, color:theme.text4 }}>+</div>
          <div style={{ fontSize:13, fontWeight:700, color:theme.text3 }}>Neue Strategie</div>
        </div>
      </Card>
    </div>;
  }

  /* ── Editor Mode ────────────────────────── */
  return <div style={{ display:"grid", gridTemplateColumns: showYaml ? "1fr 360px" : "1fr", gap:14 }}>
    <Card>
      <CardHeader
        title={<span style={{ fontSize:15 }}>{String(editFile)}</span>}
        sub={<span style={{ fontSize:12 }}>{form.inst} · {form.entry_tf}/{form.ctx_tf}/{form.regime_tf}</span>}
        right={<div style={{display:"flex",gap:6,alignItems:"center"}}>
          {mode==="yaml" && <Button onClick={() => setMode("form")}>← Builder</Button>}
          {mode==="form" && <button onClick={() => setMode("yaml")} style={{ fontSize:11, padding:"6px 10px", borderRadius:6, border:`1px solid ${theme.border}`, background:theme.white, color:theme.text4, cursor:"pointer", fontFamily:fonts.mono }} title="Direkte YAML-Bearbeitung — nur für Felder die der Builder nicht abdeckt">Raw YAML</button>}
          <Button onClick={saveExistingAsConfig}>Speichern</Button>
          <Button onClick={renameFile}>Umbenennen</Button>
          <Button onClick={cloneFile}>Clone</Button>
          <Button danger onClick={deleteFile}>Löschen</Button>
          <Button onClick={() => setEditFile(null)} style={{ color:theme.text4 }}>←</Button>
        </div>}
      />
      <div style={{ padding:"16px 20px" }}>
        {editLd ? <LoadingState/> : mode === "yaml" ? (
          <textarea value={rawYaml} onChange={e => setRawYaml(e.target.value)} style={{
            width:"100%", minHeight:500, padding:14, borderRadius:10, border:`1px solid ${theme.border}`,
            fontFamily:fonts.mono, fontSize:12, lineHeight:1.6, resize:"vertical",
            background:"#FAFAF8", color:theme.text, outline:"none", boxSizing:"border-box",
          }} spellCheck={false}/>
        ) : <ParameterForm form={form} sf={sf} showInstrument={true} artifactStatus={artifactStatus} lifecycle={lifecycle} onChange={patch=>setForm(f=>({...f,...patch}))}/>}
      </div>
    </Card>
  </div>;
}
